/**
 * Video commercial pricing — the registry, its credit math, and provider-cost lookup.
 * Import from `./pricing` (the barrel) unless you specifically want only video.
 */
import type { ELipsyncModel, VideoGenerationModelType } from "../../image/enums"
import {
  BflVideoGenerationModelType,
  EHeyGenVideoGenerationModelType,
  GoogleVideoGenerationModelType,
  GrokVideoGenerationModelType,
  HappyHorseVideoGenerationModelType,
  HunyuanVideoGenerationModelType,
  KlingAIVideoGenerationModelType,
  LtxVideoGenerationModelType,
  LumaVideoGenerationModelType,
  MinimaxVideoGenerationModelType,
  OpenAIVideoGenerationModelType,
  PixverseVideoGenerationModelType,
  RunwayVideoGenerationModelType,
  SeedanceVideoGenerationModelType,
  SkyReelsVideoGenerationModelType,
  ECreatifyVideoGenerationModelType,
  ViduVideoGenerationModelType,
  WavespeedVideoGenerationModelType,
} from "../../image/enums"
import { blg } from "../../logging"
import { calculateKieAIKlingCost, KIEAI_KLING_PRICING, KIEAI_VEO_PRICING } from "../kie-ai/pricing"
import { convertUsdToCredits, EPricingUnit } from "./pricing.shared"
import type { ModelPricing } from "./pricing.shared"
import type { VideoGenerationModelParams } from "./schemas/video"

const calculatePerSecond = (rate: number, params: VideoGenerationModelParams): number => {
  const duration =
    "duration" in params && typeof params.duration === "number"
      ? params.duration
      : "seconds" in params && typeof params.seconds === "number"
        ? params.seconds
        : 5
  return rate * duration
}

/** Published FAL tariff, checked 2026-10-22. Account $0.01/sec base quote does not prove tier quantity. */
const heyGenVideoCost = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "768p"
  const duration = "duration" in params && typeof params.duration === "number" ? params.duration : 5
  return duration * (resolution === "1080p" || resolution === "2k" ? 0.06 : 0.02)
}

const wan3ResolutionRate = (
  params: VideoGenerationModelParams,
  rates: { "480p": number; "720p": number; "1080p": number },
): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  if (resolution === "480p") return rates["480p"]
  if (resolution === "1080p") return rates["1080p"]
  return rates["720p"]
}

const calculatePerSecondWithFrames = (rate: number, params: VideoGenerationModelParams): number => {
  const fps = "frames_per_second" in params && params.frames_per_second ? params.frames_per_second : 16
  const frames = "num_frames" in params && params.num_frames ? params.num_frames : 81
  const duration = frames / fps
  return rate * duration
}

const calculatePerRequest = (rate: number, _: VideoGenerationModelParams): number => {
  return rate
}

/**
 * WaveSpeed's PER_REQUEST length-tier shape, extracted from the WAN_2_1_T2V_720P closure that
 * matched the WaveSpeed billing ledger 1.00x (VM-1089).
 *
 * Published wan-2.1 formulas read `(duration = 10) ? base_price * 1.5 : base_price`. Two things
 * that shape gets wrong if copied naively:
 *   1. `duration = 10` is an EQUALITY, not `>=` — an 8s request bills the BASE price.
 *   2. We do not submit `duration` for wan-2.1 at all; the schema exposes `num_frames`
 *      (81 @ 16fps = the base tier). So the tier keys off `frames > 81`.
 *
 * `extended` is passed explicitly rather than derived as `base * 1.5` because IEEE-754 makes
 * `0.3 * 1.5` = 0.44999999999999996, which would silently move WAN_2_1_T2V_720P's ledger-verified
 * $0.45 tier. This refactor must be value-identical for that model.
 */
const wavespeedFlatRate =
  (base: number, extended: number, baseFrames = 81) =>
  (params: VideoGenerationModelParams): number => {
    const frames = "num_frames" in params && typeof params.num_frames === "number" ? params.num_frames : baseFrames
    return frames > baseFrames ? extended : base
  }

/**
 * Grok video is billed per second of output at a per-resolution rate on both FAL and Kie.
 * 480p is the fallback because it is the CHEAPEST tier — an unrecognised resolution must never
 * resolve to a rate below what the provider actually bills.
 */
const grokVideoResolutionRate = (params: VideoGenerationModelParams, at480: number, at720: number): number =>
  ("resolution" in params ? params.resolution : "480p") === "720p" ? at720 : at480

/**
 * FAL charges for the i2v input image on top of the per-second output rate.
 *
 * Base line: "$0.002 for image input" — stated outright on the endpoint.
 * v1.5: "Each reference image adds $0.01 (1-7 supported)". The v1.5 i2v endpoint takes a single
 * `image_url` and no array, so whether that frame is billed as a "reference image" is not stated
 * unambiguously. Reserved at $0.01 deliberately: over-reserving costs a customer $0.01 on a
 * ~$0.48 clip, while under-reserving bills below provider cost, which is the failure this whole
 * reprice exists to prevent. The live-validation run's recorded provider cost settles it.
 */
const GROK_VIDEO_FAL_IMAGE_INPUT_USD = 0.002
const GROK_VIDEO_15_FAL_IMAGE_INPUT_USD = 0.01

const grokVideoHasStartImage = (params: VideoGenerationModelParams): boolean =>
  ("start_image" in params && !!params.start_image) || ("image_url" in params && !!params.image_url)

const grokVideoCost = (rate: number, params: VideoGenerationModelParams, imageInputUsd: number): number =>
  calculatePerSecond(rate, params) + (grokVideoHasStartImage(params) ? imageInputUsd : 0)

const calculatePerFrame = (rate: number, params: VideoGenerationModelParams): number => {
  const frames = "num_frames" in params ? params.num_frames : 81
  return rate * frames
}

// Helper functions for image pricing calculations

// SkyReels V4 consumer rate — CONSUMER SIDE ONLY. WaveSpeed's own bill is a mode x resolution
// matrix over a $0.10 base (see `getSkyReelsV4WavespeedRate`), up to 7x this number. VM-1089
// recorded the breach; the consumer reprice is approval-gated to VM-1089 C5.
const SKYREELS_V4_RATE = 0.02
const GEMINI_OMNI_MAX_EDIT_CONSUMER_COST = 1.6

/**
 * WaveSpeed `google/gemini-omni-flash/video-edit`:
 *   base_price * max(3, min(ceil(get_duration_v3(video)), 10)), base_price $0.16.
 *
 * The quantity is the SOURCE clip's duration, ceil'd and clamped to [3, 10] — NOT the requested
 * output `duration`, which is what we used to bill and why we under-recorded. We submit only a
 * `video_url`, so the source length is not knowable from the params; the cost is therefore
 * reserved at the 10s clamp ceiling, the same worst-case basis the consumer side already uses
 * (GEMINI_OMNI_MAX_EDIT_CONSUMER_COST, which is exactly $0.16 × 10).
 *
 * Ledger (VM-1089, 2026-08-19): 2 edit rows billed $1.60 each ($3.20) against $2.56 recorded —
 * we had priced an 8s output against a source clip that clamped to 10s.
 */
const GEMINI_OMNI_WAVESPEED_EDIT_BASE_USD = 0.16
const GEMINI_OMNI_EDIT_MIN_SOURCE_SECONDS = 3
const GEMINI_OMNI_EDIT_MAX_SOURCE_SECONDS = 10

const geminiOmniWavespeedEditCost = (sourceSeconds: number): number =>
  GEMINI_OMNI_WAVESPEED_EDIT_BASE_USD *
  Math.max(GEMINI_OMNI_EDIT_MIN_SOURCE_SECONDS, Math.min(Math.ceil(sourceSeconds), GEMINI_OMNI_EDIT_MAX_SOURCE_SECONDS))

/**
 * WaveSpeed `skywork-ai/skyreels-v4/*`:
 *   base_price * duration * (mode = "fast" ? {1080p:2.75, 720p:1.1, else:0.8}
 *                                          : {1080p:3.5,  720p:1.4, else:1.1})
 * with base_price $0.10. The per-second rates below are those products, written as literals so
 * they are exact (`0.1 * 1.4` is 0.13999999999999999 in IEEE-754).
 *
 * Only the NORMAL column is encoded. Our SkyReels schemas expose `resolution: 540p | 720p` and no
 * `mode` field at all, so a "fast" request is not constructible — the type system enforces it.
 * Normal is also the DEARER column, which is the safe default if a `mode` field is ever added.
 * (fast, for the record: 1080p $0.275/s, 720p $0.11/s, else $0.08/s.)
 *
 * Ledger (VM-1089, 2026-08-19): 4 rows (3 i2v + 1 t2v), all 720p/5s, billed $2.80 total = $0.70
 * each = 5s x $0.14/sec, against $0.40 recorded at the old flat $0.02/sec — exactly 7.00x under.
 *
 * OUT OF SCOPE: `reference-to-video` additionally carries a `ref_videos` branch in WaveSpeed's
 * published formula (multipliers up to 6.25x). The ledger has ZERO r2v rows, so there is no
 * evidence to encode it against and it is deliberately not modelled — r2v with reference videos
 * will still under-record. Tracked on the VM-1089 umbrella.
 */
const SKYREELS_V4_WAVESPEED_NORMAL_RATES = { "1080p": 0.35, "720p": 0.14, other: 0.11 } as const

const getSkyReelsV4WavespeedRate = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  if (resolution === "1080p") return SKYREELS_V4_WAVESPEED_NORMAL_RATES["1080p"]
  if (resolution === "720p") return SKYREELS_V4_WAVESPEED_NORMAL_RATES["720p"]
  return SKYREELS_V4_WAVESPEED_NORMAL_RATES.other
}

/**
 * WaveSpeed `luma/ray-3.2/{text-to-video,image-to-video}`:
 *   base_price * (duration = 10 ? 2 : 1) * (1080p ? 4 : 720p ? 2 : 1), base_price $0.50.
 *
 * FLAT PER REQUEST, not per second — and `duration = 10` is an EQUALITY, so a hypothetical 8s
 * request bills the 5s price. Our schema only offers 5 and 10, so the two are equivalent here, but
 * the equality is written literally to keep the shape faithful.
 *
 * Ledger (VM-1089, 2026-08-19): 5 rows (3 i2v + 2 t2v), all 720p/5s, billed $5.00 total = $1.00
 * each against $2.50 recorded at the old flat $0.10/sec — exactly 2.00x under.
 */
const RAY_3_2_WAVESPEED_BASE_USD = 0.5

const getWavespeedRay32Cost = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const lengthMultiplier = "duration" in params && params.duration === 10 ? 2 : 1
  const resolutionMultiplier = resolution === "1080p" ? 4 : resolution === "720p" ? 2 : 1
  return RAY_3_2_WAVESPEED_BASE_USD * lengthMultiplier * resolutionMultiplier
}

// Replicate bills Luma Ray 3.2 per video (SDR; the schema exposes no hdr/540p option). So does
// WaveSpeed, on a different tier matrix (see `getWavespeedRay32Cost`). Shared by the consumer
// floor and the replicate provider cost so the two cannot drift apart.
const getReplicateRay32Cost = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const duration = "duration" in params && params.duration === 10 ? 10 : 5
  if (resolution === "1080p") return duration === 10 ? 3.7 : 1.2
  return duration === 10 ? 0.9 : 0.3
}

const getGeminiOmniMode = (params: VideoGenerationModelParams): "t2v" | "i2v" | "r2v" | "v2v" => {
  if ("video_url" in params && params.video_url) return "v2v"
  if (
    "reference_image_urls" in params &&
    Array.isArray(params.reference_image_urls) &&
    params.reference_image_urls.length > 0
  )
    return "r2v"
  if ("image_url" in params && params.image_url) return "i2v"
  return "t2v"
}

const GEMINI_OMNI_FLASH_11_RATES_USD = {
  "360p": 0.03,
  "720p": 0.1,
  "1080p": 0.15,
  "4k": 0.3,
} as const
const getGeminiOmniFlash11ResolutionRate = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  if (resolution === "360p" || resolution === "720p" || resolution === "1080p" || resolution === "4k") {
    return GEMINI_OMNI_FLASH_11_RATES_USD[resolution]
  }
  return GEMINI_OMNI_FLASH_11_RATES_USD["720p"]
}

const calculateMeasuredVideoCost = (rate: number, params: VideoGenerationModelParams): number => {
  const actualDuration = (params as Record<string, unknown>).actualDurationSec
  return typeof actualDuration === "number" && Number.isFinite(actualDuration) && actualDuration > 0
    ? rate * actualDuration
    : calculatePerSecond(rate, params)
}

const calculateKieGeminiOmniCost = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  if ("video_url" in params && params.video_url) return resolution === "4k" ? 1.26 : 0.84
  const duration = "duration" in params && typeof params.duration === "number" ? params.duration : 8
  const normalizedDuration = duration <= 4 ? 4 : duration <= 6 ? 6 : duration <= 8 ? 8 : 10
  const standardRates = { 4: 0.315, 6: 0.42, 8: 0.525, 10: 0.63 } as const
  const fourKRates = { 4: 0.735, 6: 0.84, 8: 0.945, 10: 1.05 } as const
  return (resolution === "4k" ? fourKRates : standardRates)[normalizedDuration]
}

// Seedance 2.0 provider costs bill with-video-reference jobs at a lower per-second rate.
// Reference videos arrive under kie-style (reference_video_urls) or replicate-style (reference_videos).
const seedance2HasVideoRef = (params: VideoGenerationModelParams): boolean => {
  const p = params as Record<string, unknown>
  const kie = p.reference_video_urls
  const rep = p.reference_videos
  return (Array.isArray(kie) && kie.length > 0) || (Array.isArray(rep) && rep.length > 0)
}

/** Both providers clamp total billed reference-video input to this many seconds. */
const SEEDANCE_2_MAX_REF_INPUT_SECONDS = 15

// `reference_video_urls` (kie dialect) and `reference_videos` (replicate dialect) name the same
// input. Take the MAX rather than checking one and falling through to the other: an empty
// `reference_video_urls` alongside a populated `reference_videos` used to short-circuit to 0,
// which zeroed the input billing while `seedance2HasVideoRef` still reported a reference —
// underpricing the request. Max, not sum, so a translator that sets both can't double-count.
const seedance2ReferenceVideoCount = (params: VideoGenerationModelParams): number => {
  const p = params as Record<string, unknown>
  const kieRefs = Array.isArray(p.reference_video_urls) ? p.reference_video_urls.length : 0
  const replicateRefs = Array.isArray(p.reference_videos) ? p.reference_videos.length : 0
  return Math.max(kieRefs, replicateRefs)
}

// WaveSpeed and FAL bill reference-video INPUT seconds on top of output seconds. The clip's
// real length is NOT knowable from params — `duration` is the OUTPUT length, and the reference
// is a user-supplied URL (a free-text field, frequently external, see
// claude/skills/dev-add-model/references/pricing-floor.md). So we have two different numbers:
//
//   EXPECTED (~5s/clip) — what a typical reference costs us. Used for provider-cost TELEMETRY,
//     where the honest expectation is what makes margin reporting meaningful.
//   CEILING (15s total) — the most the providers can bill. Used for the CONSUMER rate, so a
//     long reference can never cost more than we charged.
//
// They must NOT be the same function. Pricing the customer off the expectation is what let a
// 15s reference silently erode the markup with no test able to see it: consumer and provider
// cost both moved with the estimate. Mirrors the flat worst-case ceiling already used for
// Gemini Omni v2v edits (GEMINI_OMNI_MAX_EDIT_CONSUMER_COST).
const seedance2EstimatedRefInputSeconds = (params: VideoGenerationModelParams): number => {
  const refCount = seedance2ReferenceVideoCount(params)
  if (refCount === 0) return 0
  return Math.min(SEEDANCE_2_MAX_REF_INPUT_SECONDS, Math.max(2, refCount * 5))
}

const seedance2CeilingRefInputSeconds = (params: VideoGenerationModelParams): number => {
  return seedance2ReferenceVideoCount(params) === 0 ? 0 : SEEDANCE_2_MAX_REF_INPUT_SECONDS
}

// MEASURED (real total input) — used whenever reference clips resolved to
// team-owned assets with a known durationSec (VM-978, injected as `reference_video_durations`
// by mediaGeneration.ts). Bills the real total, clamped to the providers' 2-15s billed range,
// so a typical 5s reference stops paying the 15s ceiling. Returns undefined when no measured
// duration is available (external/raw URLs), when callers retain their conservative fallback.
const seedance2MeasuredRefInputSeconds = (params: VideoGenerationModelParams): number | undefined => {
  const durations = (params as Record<string, unknown>).reference_video_durations
  if (!Array.isArray(durations) || durations.length === 0) return undefined
  const total = durations.reduce<number>(
    (sum, d) => sum + (typeof d === "number" && Number.isFinite(d) && d > 0 ? d : 0),
    0,
  )
  if (total <= 0) return undefined
  return Math.min(SEEDANCE_2_MAX_REF_INPUT_SECONDS, Math.max(2, total))
}

/**
 * Which reference-input length a Seedance rate is computed against.
 * "expected" = provider-cost fallback. "ceiling" = consumer-rate fallback.
 */
type RefInputBasis = "expected" | "ceiling"

/**
 * Cost meters multiply the returned rate by OUTPUT seconds, so with-ref rates are scaled by
 * (output + billed input) / output to fold in the input-second billing.
 *
 * @param basis - fallback when no resolved reference duration exists.
 */
const seedance2RefInputMultiplier = (
  params: VideoGenerationModelParams,
  defaultDuration: number,
  basis: RefInputBasis = "expected",
): number => {
  const p = params as Record<string, unknown>
  const rawDuration = typeof p.duration === "number" ? p.duration : Number(p.duration)
  const outputSeconds = Number.isFinite(rawDuration) && rawDuration >= 4 ? rawDuration : defaultDuration
  // A resolved duration is provider billing truth. Only unknown/external references use the
  // conservative consumer ceiling or provider expected-duration fallback.
  const inputSeconds =
    seedance2MeasuredRefInputSeconds(params) ??
    (basis === "ceiling" ? seedance2CeilingRefInputSeconds(params) : seedance2EstimatedRefInputSeconds(params))
  return (outputSeconds + inputSeconds) / outputSeconds
}

const seedance2OutputSeconds = (params: VideoGenerationModelParams, fallback: number): number => {
  const duration = (params as Record<string, unknown>).duration
  const parsed = typeof duration === "number" ? duration : Number(duration)
  return Number.isFinite(parsed) && parsed >= 4 ? parsed : fallback
}

/**
 * FAL's documented video-input billing rate for Seedance 2 MINI: reference-video INPUT
 * seconds bill at 0.6x the output per-second rate. Mini is the only variant that discounts
 * input — the full and Fast variants bill input seconds at the full output rate (factor 1),
 * which is why their rate functions apply `seedance2RefInputMultiplier` unscaled.
 */
const SEEDANCE_2_MINI_FAL_REF_INPUT_FACTOR = 0.6

// ---------------------------------------------------------------------------------------
// Seedance 2.0 per-provider rates.
//
// Each of these is the SINGLE definition of that provider's per-second rate: the
// `providerCosts` entry below uses it, and so does the model's consumer `rate`, which is
// `Math.max(...)` over the production-routable providers. Sharing the function is what makes
// "consumer price == dearest routable provider cost" true by construction instead of by a
// hand-copied number that drifts the next time a provider repricess.
//
// All Seedance 2 provider entries are PER_SECOND with the same duration basis, so a max over
// RATES is equivalent to a max over COSTS.
// ---------------------------------------------------------------------------------------

const seedance2ReplicateRate = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const hasVideoRef = seedance2HasVideoRef(params)
  const base = resolution === "480p" ? (hasVideoRef ? 0.1 : 0.08) : hasVideoRef ? 0.22 : 0.18
  if (!hasVideoRef) return base
  const measuredInputSeconds = seedance2MeasuredRefInputSeconds(params)
  if (measuredInputSeconds === undefined) return base
  const outputSeconds = seedance2OutputSeconds(params, 8)
  const expectedInputSeconds = seedance2EstimatedRefInputSeconds(params)
  return base * ((outputSeconds + measuredInputSeconds) / (outputSeconds + expectedInputSeconds))
}

/**
 * WaveSpeed's published `bytedance/seedance-2.0/*` catalog entry carries `discount_rate: 90`, i.e.
 * we are billed 90% of list. NOTE the inversion: `100` means NO discount, `90` means 10% off.
 *
 * Ledger (VM-1089, 2026-08-19): 12 rows across the three 2.0 endpoints (t2v/i2v/r2v) billed
 * $18.4320 against $20.4800 recorded — a uniform 0.90x on every row, which is what makes the
 * discount safe to rely on rather than an account-specific one-off.
 *
 * Applied ONLY in the `providerCosts.wavespeed` entry, never inside `seedance2WavespeedRate`
 * itself, because that function also feeds the consumer `rate` (a `Math.max` over routable
 * providers). Discounting the shared function could lower a consumer price, which is gated to
 * VM-1089 C5. `seedance-2.0-mini` and `seedance-2.0-fast` are deliberately NOT discounted here:
 * mini/image-to-video matched the ledger 1.00x, and the fast endpoints have no ledger rows at all.
 */
const SEEDANCE_2_WAVESPEED_DISCOUNT = 0.9

const seedance2WavespeedRate = (params: VideoGenerationModelParams, basis: RefInputBasis = "expected"): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const hasVideoRef = seedance2HasVideoRef(params)
  const base = resolution === "480p" ? (hasVideoRef ? 0.075 : 0.12) : hasVideoRef ? 0.15 : 0.24
  return hasVideoRef ? base * seedance2RefInputMultiplier(params, 8, basis) : base
}

const seedance2FalRate = (params: VideoGenerationModelParams, basis: RefInputBasis = "expected"): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const hasVideoRef = seedance2HasVideoRef(params)
  const base = resolution === "480p" ? (hasVideoRef ? 0.0807 : 0.1345) : hasVideoRef ? 0.1814 : 0.3034
  return hasVideoRef ? base * seedance2RefInputMultiplier(params, 8, basis) : base
}

const seedance2FastReplicateRate = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const hasVideoRef = seedance2HasVideoRef(params)
  const base = resolution === "480p" ? (hasVideoRef ? 0.08 : 0.07) : hasVideoRef ? 0.17 : 0.15
  if (!hasVideoRef) return base
  const measuredInputSeconds = seedance2MeasuredRefInputSeconds(params)
  if (measuredInputSeconds === undefined) return base
  const outputSeconds = seedance2OutputSeconds(params, 8)
  const expectedInputSeconds = seedance2EstimatedRefInputSeconds(params)
  return base * ((outputSeconds + measuredInputSeconds) / (outputSeconds + expectedInputSeconds))
}

const seedance2FastWavespeedRate = (params: VideoGenerationModelParams, basis: RefInputBasis = "expected"): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const hasVideoRef = seedance2HasVideoRef(params)
  const base = resolution === "480p" ? (hasVideoRef ? 0.065 : 0.1) : hasVideoRef ? 0.13 : 0.2
  return hasVideoRef ? base * seedance2RefInputMultiplier(params, 8, basis) : base
}

const seedance2FastFalRate = (params: VideoGenerationModelParams, basis: RefInputBasis = "expected"): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const hasVideoRef = seedance2HasVideoRef(params)
  const base = resolution === "480p" ? (hasVideoRef ? 0.0645 : 0.1076) : hasVideoRef ? 0.14515 : 0.2419
  return hasVideoRef ? base * seedance2RefInputMultiplier(params, 8, basis) : base
}

const seedance2MiniReplicateRate = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const hasVideoRef = seedance2HasVideoRef(params)
  return resolution === "480p" ? (hasVideoRef ? 0.05 : 0.04) : hasVideoRef ? 0.11 : 0.09
}

const seedance2MiniKieRate = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const hasVideoRef = seedance2HasVideoRef(params)
  return resolution === "480p" ? (hasVideoRef ? 0.03 : 0.0475) : hasVideoRef ? 0.0625 : 0.1025
}

const seedance2MiniWavespeedRate = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  return resolution === "480p" ? 0.06 : 0.12
}

const seedance2MiniFalRate = (params: VideoGenerationModelParams, basis: RefInputBasis = "expected"): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const base = resolution === "480p" ? 0.0721 : 0.1547
  if (!seedance2HasVideoRef(params)) return base
  return base * (1 + SEEDANCE_2_MINI_FAL_REF_INPUT_FACTOR * (seedance2RefInputMultiplier(params, 5, basis) - 1))
}

// ---------------------------------------------------------------------------------------
// Seedance 2.5. Deliberately NOT reusing the 2.0 helpers: 2.0 clamps billed reference input at
// 15s, but fal's 2.5 reference-to-video schema allows a combined 30.2s — reusing the 2.0 clamp
// would under-bill the consumer ceiling on a long reference by half.
// ---------------------------------------------------------------------------------------

/** fal 2.5: combined reference-video duration must not exceed 30.2s; billed from a 2s floor. */
const SEEDANCE_2_5_MAX_REF_INPUT_SECONDS = 30

/** fal 2.5 multiplies the whole price by 0.6 when video references are supplied. */
const SEEDANCE_2_5_FAL_REF_INPUT_FACTOR = 0.6

const seedance25RefInputSeconds = (params: VideoGenerationModelParams, basis: RefInputBasis): number => {
  if (!seedance2HasVideoRef(params)) return 0
  const durations = (params as Record<string, unknown>).reference_video_durations
  if (Array.isArray(durations) && durations.length > 0) {
    const total = durations.reduce<number>(
      (sum, d) => sum + (typeof d === "number" && Number.isFinite(d) && d > 0 ? d : 0),
      0,
    )
    // A resolved duration is provider billing truth; only unknown/external refs use a fallback.
    if (total > 0) return Math.min(SEEDANCE_2_5_MAX_REF_INPUT_SECONDS, Math.max(2, total))
  }
  if (basis === "ceiling") return SEEDANCE_2_5_MAX_REF_INPUT_SECONDS
  return Math.min(SEEDANCE_2_5_MAX_REF_INPUT_SECONDS, Math.max(2, seedance2ReferenceVideoCount(params) * 5))
}

/** Cost meters multiply by OUTPUT seconds, so fold billed input seconds into the rate. */
const seedance25RefInputMultiplier = (params: VideoGenerationModelParams, basis: RefInputBasis): number => {
  const outputSeconds = seedance2OutputSeconds(params, 5)
  return (outputSeconds + seedance25RefInputSeconds(params, basis)) / outputSeconds
}

/** fal: $0.2205/s (480p), $0.4730/s (720p). Video refs scale by 0.6 AND bill input seconds. */
const seedance25FalRate = (params: VideoGenerationModelParams, basis: RefInputBasis = "expected"): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const base = resolution === "480p" ? 0.2205 : 0.473
  if (!seedance2HasVideoRef(params)) return base
  return base * SEEDANCE_2_5_FAL_REF_INPUT_FACTOR * seedance25RefInputMultiplier(params, basis)
}

/**
 * replicate: four flat per-OUTPUT-second tiers from the model page's billingConfig
 * (model variant non_video_in/video_in x 480p/720p). Input seconds are already priced into the
 * video_in tier, so there is no multiplier here — unlike fal, replicate charges MORE with video
 * references, which is why the consumer rate is a per-tier max() and not a single provider's rate.
 */
const seedance25ReplicateRate = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  const hasVideoRef = seedance2HasVideoRef(params)
  if (resolution === "480p") return hasVideoRef ? 0.4304 : 0.1028
  return hasVideoRef ? 0.9676 : 0.2312
}

/** fal caps combined reference video/audio at 15s and bills each clip from a 2s floor. */
const MINIMAX_H3_MAX_REF_INPUT_SECONDS = 15
const MINIMAX_H3_MIN_REF_INPUT_SECONDS = 2

/**
 * Billable seconds of reference VIDEO ($0.26/sec at 2K, same rate as output).
 *
 * MEASURED whenever the clips resolved to team-owned assets with a known `durationSec` —
 * `mediaGeneration.ts` injects those as `reference_video_durations` (VM-978), the same channel
 * Seedance 2.0 bills from. Falls back to the 15s CEILING for raw/external URLs, so an
 * unmeasurable reference over-bills slightly rather than silently costing us the difference.
 */
const minimaxH3ReferenceVideoSeconds = (params: Record<string, unknown>): number => {
  const urls = params.reference_video_urls
  if (!Array.isArray(urls) || urls.length === 0) return 0

  const durations = params.reference_video_durations
  // Trust the measured path ONLY when EVERY clip is accounted for. Partial metadata — some clips
  // resolved to owned assets, others raw URLs — must not bill just the known ones, which would
  // silently under-bill the rest. All-or-nothing; otherwise the conservative ceiling.
  const fullyMeasured =
    Array.isArray(durations) &&
    durations.length === urls.length &&
    durations.every((d) => typeof d === "number" && Number.isFinite(d) && d > 0)

  if (fullyMeasured) {
    const total = (durations as number[]).reduce<number>((sum, d) => sum + d, 0)
    return Math.min(MINIMAX_H3_MAX_REF_INPUT_SECONDS, Math.max(MINIMAX_H3_MIN_REF_INPUT_SECONDS, total))
  }

  return MINIMAX_H3_MAX_REF_INPUT_SECONDS
}

/**
 * MiniMax H3 billing, per fal's published rates (2026-07-31):
 * $0.26 per output second at 2K, $0.08 per reference image past the first 5, and $0.26 per
 * second of reference video. Audio references are free.
 */
const minimaxH3Cost = (params: Record<string, unknown>): number => {
  const rawDuration = "duration" in params ? Number(params.duration) : NaN
  // fal clamps to 5..15 and defaults to 5; mirror that so an absent/garbage value never
  // under-bills a real generation.
  const seconds = Number.isFinite(rawDuration) ? Math.min(15, Math.max(5, rawDuration)) : 5

  const referenceImages =
    "reference_image_urls" in params && Array.isArray(params.reference_image_urls)
      ? params.reference_image_urls.length
      : 0
  const billableImages = Math.max(0, referenceImages - 5)

  return 0.26 * seconds + 0.08 * billableImages + 0.26 * minimaxH3ReferenceVideoSeconds(params)
}

const borealRate = (params: VideoGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "720p"
  return resolution === "2k" ? 0.12 : resolution === "1080p" ? 0.03 : 0.01
}

export const replicateModelPricing: Partial<Record<VideoGenerationModelType, ModelPricing>> = {
  [EHeyGenVideoGenerationModelType.HEYGEN_VIDEO]: {
    rate: (params) => heyGenVideoCost(params),
    unit: EPricingUnit.PER_REQUEST,
    description: "HeyGen Video: $0.02/sec at 480p/768p; $0.06/sec at 1080p/2k",
    providerCosts: {
      fal: {
        rate: (params) => heyGenVideoCost(params),
        unit: EPricingUnit.PER_REQUEST,
        description: "FAL published tariff estimate; account base-unit quantity unconfirmed",
      },
    },
  },
  [ECreatifyVideoGenerationModelType.BOREAL]: {
    rate: borealRate,
    unit: EPricingUnit.PER_SECOND,
    description:
      "FAL Boreal: 720p $0.01/s, 1080p $0.03/s, 2k $0.12/s; requested-duration estimate, metering unverified",
    providerCosts: {
      fal: { rate: borealRate, unit: EPricingUnit.PER_SECOND, description: "Official FAL per-second resolution tiers" },
    },
  },
  [GoogleVideoGenerationModelType.GEMINI_OMNI_FLASH_PREVIEW]: {
    // Priced off WaveSpeed, not FAL: FAL is the primary but WaveSpeed is dearer in every
    // mode ($0.13/$0.14/$0.16 vs $0.125/$0.13), so pricing at FAL alone would sell any
    // WaveSpeed-routed generation under the 20% floor.
    rate: (params) => {
      const mode = getGeminiOmniMode(params)
      if (mode === "t2v") return 0.13
      if (mode === "i2v") return 0.14
      return 0.16
    },
    unit: EPricingUnit.PER_SECOND,
    description:
      "Gemini Omni Flash: $0.13/sec T2V, $0.14/sec I2V, $0.16/sec R2V (WaveSpeed worst case); " +
      "edits reserve the 10s source maximum ($1.60)",
    calculateCost: (rate, params) =>
      getGeminiOmniMode(params) === "v2v" ? GEMINI_OMNI_MAX_EDIT_CONSUMER_COST : calculatePerSecond(rate, params),
    providerCosts: {
      fal: {
        rate: (params) => (getGeminiOmniMode(params) === "t2v" ? 0.125 : 0.13),
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.125/sec T2V; $0.13/sec I2V/R2V or edit output",
      },
      wavespeed: {
        rate: (params) => {
          const mode = getGeminiOmniMode(params)
          if (mode === "t2v") return 0.13
          if (mode === "i2v") return 0.14
          return 0.16
        },
        unit: EPricingUnit.PER_SECOND,
        // T2V/I2V/R2V bill per OUTPUT second and were ledger-verified 1.00x — untouched.
        // The v2v edit is a different formula and is delegated to `geminiOmniWavespeedEditCost`.
        //
        // `getVideoProviderBilling` keeps reporting PER_SECOND x output seconds for this entry
        // while the edit's dollars come from the source clip. That divergence is the documented
        // contract for any entry with a custom `calculateCost` (see that function's doc block, and
        // the pre-existing kie-ai entry below): quantity is a USAGE fact, and dollars for these
        // models are not a plain rate x quantity product.
        calculateCost: (rate, params) =>
          getGeminiOmniMode(params) === "v2v"
            ? geminiOmniWavespeedEditCost(GEMINI_OMNI_EDIT_MAX_SOURCE_SECONDS)
            : calculatePerSecond(rate, params),
        description:
          "WaveSpeed: $0.13/$0.14/$0.16 per sec for T2V/I2V/R2V; a source-video edit bills " +
          "$0.16 × clamp(ceil(source seconds), 3, 10), reserved at the 10s maximum ($1.60)",
      },
      "kie-ai": {
        rate: 0,
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: duration/resolution flat-rate matrix",
        calculateCost: (_rate, params) => calculateKieGeminiOmniCost(params),
      },
    },
  },
  [GoogleVideoGenerationModelType.GEMINI_OMNI_FLASH_1_1]: {
    rate: getGeminiOmniFlash11ResolutionRate,
    unit: EPricingUnit.PER_SECOND,
    description: "Gemini Omni Flash 1.1: $0.03/$0.10/$0.15/$0.30 per output second at 360p/720p/1080p/4K",
    calculateCost: (rate, params) => calculatePerSecond(rate, params),
    providerCosts: {
      fal: {
        rate: getGeminiOmniFlash11ResolutionRate,
        unit: EPricingUnit.PER_SECOND,
        requiresActualDurationSec: true,
        calculateCost: (rate, params) => calculateMeasuredVideoCost(rate, params),
        description: "FAL generation: output-duration settlement at $0.03/$0.10/$0.15/$0.30 per second",
      },
      wavespeed: {
        rate: getGeminiOmniFlash11ResolutionRate,
        unit: EPricingUnit.PER_SECOND,
        requiresActualDurationSec: true,
        calculateCost: (rate, params) => calculateMeasuredVideoCost(rate, params),
        description: "WaveSpeed generation: output-duration settlement at $0.03/$0.10/$0.15/$0.30 per second",
      },
    },
  },
  // Kling 1.0 models (via FAL.ai)
  [KlingAIVideoGenerationModelType.KLING_1_0_STANDARD]: {
    rate: 0.045,
    unit: EPricingUnit.PER_SECOND,
    description: "Kling 1.0 Standard: $0.045 per second",
    providerCosts: {
      fal: { rate: 0.045, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.045/sec" },
    },
  },
  [KlingAIVideoGenerationModelType.KLING_1_0_PRO]: {
    rate: 0.085,
    unit: EPricingUnit.PER_SECOND,
    description: "Kling 1.0 Pro: $0.085 per second (estimated)",
    providerCosts: {
      fal: { rate: 0.085, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.085/sec" },
    },
  },

  // Kling models (Replicate)
  [KlingAIVideoGenerationModelType.KLING_STANDARD_1_6]: {
    rate: 0.05,
    unit: EPricingUnit.PER_SECOND,
    description: "kling-v1.6-standard: $0.05 per second", // eslint-disable-line @aix/no-hardcoded-model-strings
    providerCosts: {
      replicate: { rate: 0.05, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.05/sec" },
      fal: { rate: 0.05, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.05/sec" },
    },
  },
  [KlingAIVideoGenerationModelType.KLING_PRO_1_6]: {
    rate: 0.095,
    unit: EPricingUnit.PER_SECOND,
    description: "kling-v1.6-pro: $0.095 per second", // eslint-disable-line @aix/no-hardcoded-model-strings
    providerCosts: {
      replicate: { rate: 0.095, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.095/sec" },
      fal: { rate: 0.095, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.095/sec" },
    },
  },
  [KlingAIVideoGenerationModelType.KLING_2_1]: {
    rate: (params) => {
      const mode = "mode" in params ? params.mode : "standard"
      return mode === "pro" ? 0.09 : 0.05
    },
    unit: EPricingUnit.PER_SECOND,
    description: "kling-v2.1: $0.05/sec (standard) or $0.09/sec (pro)", // eslint-disable-line @aix/no-hardcoded-model-strings
    providerCosts: {
      replicate: {
        rate: (params) => {
          const mode = "mode" in params ? params.mode : "standard"
          return mode === "pro" ? 0.09 : 0.05
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.05/sec (standard) or $0.09/sec (pro)",
      },
      fal: {
        rate: (params) => {
          const mode = "mode" in params ? params.mode : "standard"
          return mode === "pro" ? 0.09 : 0.05
        },
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.05/sec (standard) or $0.09/sec (pro)",
      },
      "kie-ai": {
        rate: 0, // Flat-rate, handled by calculateCost
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: flat rate per video",
        calculateCost: (_rate, params) => {
          const duration =
            "duration" in params && typeof params.duration === "number" ? (params.duration >= 10 ? 10 : 5) : 5
          const hasAudio = "generate_audio" in params ? !!params.generate_audio : false
          return calculateKieAIKlingCost(duration, hasAudio)
        },
      },
    },
  },
  [KlingAIVideoGenerationModelType.KLING_2_1_MASTER]: {
    rate: 0.12,
    unit: EPricingUnit.PER_SECOND,
    description: "kling-v2.1-master: $0.12 per second (premium master tier)", // eslint-disable-line @aix/no-hardcoded-model-strings
    providerCosts: {
      replicate: { rate: 0.12, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.12/sec" },
      fal: { rate: 0.12, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.12/sec" },
    },
  },

  // Kling 2.6 — FAL: $0.07/sec i2v, Kie.ai: flat rate
  [KlingAIVideoGenerationModelType.KLING_2_6]: {
    rate: 0.07,
    unit: EPricingUnit.PER_SECOND,
    description: "Kling 2.6: $0.07/sec (cinematic visuals, native audio)",
    providerCosts: {
      fal: { rate: 0.07, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.07/sec" },
      "kie-ai": {
        rate: 0,
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: flat rate per video",
        calculateCost: (_rate, params) => {
          const duration =
            "duration" in params && typeof params.duration === "number" ? (params.duration >= 10 ? 10 : 5) : 5
          const hasAudio = "generate_audio" in params ? !!params.generate_audio : false
          return calculateKieAIKlingCost(duration, hasAudio)
        },
      },
    },
  },
  [KlingAIVideoGenerationModelType.KLING_2_5_TURBO_PRO]: {
    rate: 0.07,
    unit: EPricingUnit.PER_SECOND,
    description: "kling-v2.5-turbo-pro: $0.07 per second", // eslint-disable-line @aix/no-hardcoded-model-strings
    providerCosts: {
      replicate: { rate: 0.07, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.07/sec" },
      fal: { rate: 0.07, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.07/sec" },
      "kie-ai": {
        rate: 0,
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: flat rate per video",
        calculateCost: (_rate, params) => {
          const duration =
            "duration" in params && typeof params.duration === "number" ? (params.duration >= 10 ? 10 : 5) : 5
          const hasAudio = "generate_audio" in params ? !!params.generate_audio : false
          return calculateKieAIKlingCost(duration, hasAudio)
        },
      },
    },
  },
  [KlingAIVideoGenerationModelType.KLING_3_0_OMNI]: {
    // No FAL endpoint — Replicate is the only routable provider. Rate held at ~50% margin
    // over Replicate's $0.168/sec base (well above the 20% floor); product decision to keep
    // the existing margin rather than reprice to the floor.
    rate: 0.336,
    unit: EPricingUnit.PER_SECOND,
    description: "kling-v3.0-omni: $0.336 per second (multimodal, native audio, T2V + I2V)", // eslint-disable-line @aix/no-hardcoded-model-strings
    providerCosts: {
      // Replicate tiers ($0.168/$0.224/$0.28/$0.42 incl. 4K+audio) don't map cleanly to the
      // schema's mode/generate_audio params (no 4K param), so use the $0.168 base. See DRIFT REPORT.
      replicate: { rate: 0.168, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.168/sec (base tier)" },
    },
  },
  [KlingAIVideoGenerationModelType.KLING_3_0]: {
    rate: (params) => {
      const mode = "mode" in params ? params.mode : "standard"
      const hasAudio = "generate_audio" in params ? params.generate_audio : false
      // FAL pricing: standard $0.084/sec, pro $0.112/sec (+audio surcharge)
      if (mode === "pro") return hasAudio ? 0.168 : 0.112
      return hasAudio ? 0.126 : 0.084
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Kling V3: $0.084-0.168/sec depending on tier and audio",
    providerCosts: {
      fal: {
        rate: (params) => {
          const mode = "mode" in params ? params.mode : "standard"
          const hasAudio = "generate_audio" in params ? params.generate_audio : false
          if (mode === "pro") return hasAudio ? 0.168 : 0.112
          return hasAudio ? 0.126 : 0.084
        },
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.084-0.168/sec",
      },
      replicate: {
        rate: (params) => {
          const mode = "mode" in params ? params.mode : "standard"
          const hasAudio = "generate_audio" in params ? params.generate_audio : false
          if (mode === "pro") return hasAudio ? 0.168 : 0.112
          return hasAudio ? 0.126 : 0.084
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.084-0.168/sec",
      },
      "kie-ai": {
        rate: 0,
        unit: EPricingUnit.PER_REQUEST,
        description: `Kie.ai: $${KIEAI_KLING_PRICING["5s"]}/5s, $${KIEAI_KLING_PRICING["10s"]}/10s`,
        calculateCost: (_rate, params) => {
          const duration =
            "duration" in params && typeof params.duration === "number" ? (params.duration >= 10 ? 10 : 5) : 5
          const hasAudio = "generate_audio" in params ? !!params.generate_audio : false
          return calculateKieAIKlingCost(duration, hasAudio)
        },
      },
    },
  },
  [KlingAIVideoGenerationModelType.KLING_4_0]: {
    // WaveSpeed machine formula observed 2026-10-22: (resolution = 1080p ? 235000 : 157000)
    // * (duration ?? 5), in micro-USD. Scanner pricePerSec=0.0314 is wrong: it divides
    // the $0.157 per-second base by five. Provider invoice remains unobserved.
    rate: (params) => ("resolution" in params && params.resolution === "1080p" ? 0.235 : 0.157),
    unit: EPricingUnit.PER_SECOND,
    description: "Kling 4 standard: $0.157/sec 720p, $0.235/sec 1080p",
    providerCosts: {
      wavespeed: {
        rate: (params) => ("resolution" in params && params.resolution === "1080p" ? 0.235 : 0.157),
        unit: EPricingUnit.PER_SECOND,
        requiresActualDurationSec: true,
        calculateCost: (rate, params) => calculateMeasuredVideoCost(rate, params),
        description: "WaveSpeed official output-second formula (2026-10-22)",
      },
    },
  },
  // Kling O3 (Video 3.0 Omni) - FAL pricing
  [KlingAIVideoGenerationModelType.KLING_O3]: {
    rate: (params) => {
      const mode = "mode" in params ? params.mode : "standard"
      const hasAudio = "generate_audio" in params ? params.generate_audio : false
      if (mode === "pro") return hasAudio ? 0.14 : 0.112
      return hasAudio ? 0.112 : 0.084
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Kling O3: $0.084-0.14/sec depending on tier and audio",
    providerCosts: {
      fal: {
        rate: (params) => {
          const mode = "mode" in params ? params.mode : "standard"
          const hasAudio = "generate_audio" in params ? params.generate_audio : false
          if (mode === "pro") return hasAudio ? 0.14 : 0.112
          return hasAudio ? 0.112 : 0.084
        },
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.084-0.14/sec",
      },
    },
  },
  // Kling O3 4K (FAL) - 4K premium over standard O3 (~40% premium)
  [KlingAIVideoGenerationModelType.KLING_O3_4K]: {
    rate: (params) => {
      const mode = "mode" in params ? params.mode : "standard"
      const hasAudio = "generate_audio" in params ? params.generate_audio : false
      if (mode === "pro") return hasAudio ? 0.196 : 0.157
      return hasAudio ? 0.157 : 0.118
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Kling O3 4K: $0.118-0.196/sec (4K output, depends on tier and audio)",
    providerCosts: {
      fal: {
        rate: (params) => {
          const mode = "mode" in params ? params.mode : "standard"
          const hasAudio = "generate_audio" in params ? params.generate_audio : false
          if (mode === "pro") return hasAudio ? 0.196 : 0.157
          return hasAudio ? 0.157 : 0.118
        },
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.118-0.196/sec (4K output)",
      },
    },
  },
  // Kling 3.0 4K (FAL) - 4K premium over standard 3.0 (~40% premium)
  [KlingAIVideoGenerationModelType.KLING_3_0_4K]: {
    rate: (params) => {
      const mode = "mode" in params ? params.mode : "standard"
      const hasAudio = "enable_audio" in params ? params.enable_audio : false
      if (mode === "pro") return hasAudio ? 0.235 : 0.157
      return hasAudio ? 0.176 : 0.118
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Kling 3.0 4K: $0.118-0.235/sec (4K output, depends on tier and audio)",
    providerCosts: {
      fal: {
        rate: (params) => {
          const mode = "mode" in params ? params.mode : "standard"
          const hasAudio = "enable_audio" in params ? params.enable_audio : false
          if (mode === "pro") return hasAudio ? 0.235 : 0.157
          return hasAudio ? 0.176 : 0.118
        },
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.118-0.235/sec (4K output)",
      },
    },
  },
  // Kling 2.6 Motion Control (Kie.ai) - V2V motion transfer, $0.09/sec (1080p), $0.055/sec (720p)
  [KlingAIVideoGenerationModelType.KLING_2_6_MOTION_CONTROL]: {
    rate: (params) => {
      const mode = "mode" in params ? params.mode : "720p"
      return mode === "1080p" ? 0.09 : 0.055
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Kling 2.6 Motion Control: $0.055/sec (720p) or $0.09/sec (1080p) via Kie.ai",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const mode = "mode" in params ? params.mode : "720p"
          return mode === "1080p" ? 0.09 : 0.055
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Kie.ai: $0.055/sec (720p) or $0.09/sec (1080p)",
      },
    },
  },
  // Kling 3.0 Motion Control - per-request pricing
  [KlingAIVideoGenerationModelType.KLING_3_0_MOTION_CONTROL]: {
    rate: (params) => {
      const mode = "mode" in params ? params.mode : "720p"
      return mode === "1080p" ? 0.55 : 0.28
    },
    unit: EPricingUnit.PER_REQUEST,
    description: "Kling 3.0 Motion Control: $0.28 (720p) or $0.55 (1080p) per video",
    providerCosts: {
      fal: {
        rate: (params) => {
          const mode = "mode" in params ? params.mode : "720p"
          return mode === "1080p" ? 0.55 : 0.28
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "FAL: $0.28 (720p) or $0.55 (1080p)",
      },
      "kie-ai": {
        rate: (params) => {
          const mode = "mode" in params ? params.mode : "720p"
          return mode === "1080p" ? 0.55 : 0.28
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: $0.28 (720p) or $0.55 (1080p)",
      },
    },
  },

  /**
   * MiniMax H3 — $0.26/sec at 2K on every mode (t2v / i2v / r2v).
   *
   * FAL-only, so the pricing floor is trivially satisfied: the sole routable production provider
   * bills $0.26/sec and the consumer rate matches it, leaving the 1.2× markup as a real 16.7%
   * gross margin rather than break-even (a 1.2× MARKUP on cost is a 16.7% MARGIN on revenue —
   * 0.2/1.2 — which is the number `margin-matrix` prints for every H3 row). There is no
   * cheap-primary/dearer-fallback split to leak on (the `openai/sora-2` failure mode) because
   * there is exactly one provider.
   *
   * FAL pricing text, fetched 2026-07-31 from https://fal.ai/api/models:
   *   t2v: "At an output resolution of 2K, every second of video costs $0.26."
   *   i2v: same, plus "Image input is free."
   *   r2v: same, plus "Audio references are free, the first 5 reference images are free and each
   *        additional image costs $0.08, and reference video costs $0.26 per second at 2K."
   *
   * Every documented dimension is billed: output seconds, reference images past the free 5, and
   * reference-video seconds (measured from `reference_video_durations` when the clips are
   * team-owned assets, else the 15s ceiling). Audio references are free per fal.
   */
  [MinimaxVideoGenerationModelType.H3]: {
    rate: (params) => minimaxH3Cost(params),
    unit: EPricingUnit.PER_REQUEST,
    description: "MiniMax H3: $0.26/sec at 2K; reference images beyond the first 5 add $0.08 each",
    providerCosts: {
      fal: {
        rate: (params) => minimaxH3Cost(params),
        unit: EPricingUnit.PER_REQUEST,
        description: "FAL: $0.26/sec at 2K + $0.08 per reference image beyond the first 5",
      },
    },
  },

  // Minimax Hailuo 2.3 models
  [MinimaxVideoGenerationModelType.HAILUO_2_3]: {
    // No FAL endpoint — Replicate is the only routable provider. Rate held at ~50% margin
    // over Replicate ($0.28 6s / $0.56 10s); product decision to keep the existing margin.
    rate: (params) => {
      const duration = "duration" in params && params.duration === 10 ? 10 : 6
      return duration === 10 ? 1.12 : 0.56
    },
    unit: EPricingUnit.PER_REQUEST,
    description: "Minimax Hailuo 2.3: $0.56 (6s) or $1.12 (10s) per video",
    providerCosts: {
      // Schema has no resolution param, so 6s uses the low-res tier ($0.28); high-res 6s ($0.49) unreachable.
      replicate: {
        rate: (params) => {
          const duration = "duration" in params && params.duration === 10 ? 10 : 6
          return duration === 10 ? 0.56 : 0.28
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Replicate: $0.28 (6s low-res) or $0.56 (10s) per video",
      },
    },
  },
  [MinimaxVideoGenerationModelType.HAILUO_2_3_FAST]: {
    // No FAL endpoint — Replicate is the only routable provider. Rate held at ~50% margin
    // over Replicate ($0.19 6s / $0.32 10s); product decision to keep the existing margin.
    rate: (params) => {
      const duration = "duration" in params && params.duration === 10 ? 10 : 6
      return duration === 10 ? 0.64 : 0.38
    },
    unit: EPricingUnit.PER_REQUEST,
    description: "Minimax Hailuo 2.3 Fast: $0.38 (6s) or $0.64 (10s) per video",
    providerCosts: {
      // Schema has no resolution param, so 6s uses the low-res tier ($0.19); high-res 6s ($0.33) unreachable.
      replicate: {
        rate: (params) => {
          const duration = "duration" in params && params.duration === 10 ? 10 : 6
          return duration === 10 ? 0.32 : 0.19
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Replicate: $0.19 (6s low-res) or $0.32 (10s) per video",
      },
    },
  },

  // Minimax - fixed price per video
  [MinimaxVideoGenerationModelType.VIDEO_01]: {
    rate: 0.5,
    unit: EPricingUnit.PER_REQUEST,
    description: "Minimax Video-01: $0.50 per output video (6 seconds fixed)",
    providerCosts: {
      replicate: { rate: 0.5, unit: EPricingUnit.PER_REQUEST, description: "Replicate: $0.50/video" },
      fal: { rate: 0.5, unit: EPricingUnit.PER_REQUEST, description: "FAL: $0.50/video" },
    },
  },
  // Minimax Video-01 Director - camera movement commands in prompt
  [MinimaxVideoGenerationModelType.VIDEO_01_DIRECTOR]: {
    rate: 0.5,
    unit: EPricingUnit.PER_REQUEST,
    description: "Minimax Video-01 Director: $0.50 per video (6s fixed, with camera controls)",
    providerCosts: {
      replicate: { rate: 0.5, unit: EPricingUnit.PER_REQUEST, description: "Replicate: $0.50/video (flat)" },
    },
  },
  // Minimax Video-01 Live - I2V for animation/Live2D
  [MinimaxVideoGenerationModelType.VIDEO_01_LIVE]: {
    rate: 0.5,
    unit: EPricingUnit.PER_REQUEST,
    description: "Minimax Video-01 Live: $0.50 per video (optimized for animation/Live2D)",
    providerCosts: {
      replicate: { rate: 0.5, unit: EPricingUnit.PER_REQUEST, description: "Replicate: $0.50/video (flat)" },
    },
  },
  // Minimax Hailuo 02 - standard quality (non-fast), higher resolution
  [MinimaxVideoGenerationModelType.HAILUO_02]: {
    // Priced at FAL's $0.045/sec — mirroring FAL's own entry rather than re-tiering per
    // duration, so the two cannot drift. FAL is dearer than Replicate's flat $0.15/$0.25
    // per video at both allowed durations (6s $0.27, 10s $0.45), so it sets the rate.
    rate: 0.045,
    unit: EPricingUnit.PER_SECOND,
    description: "Minimax Hailuo 02: $0.045/sec (FAL) — $0.27 for 6s, $0.45 for 10s",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const duration = "duration" in params && params.duration === 10 ? 10 : 6
          return duration === 10 ? 0.25 : 0.15
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: $0.15 (6s) / $0.25 (10s) — 44% cheaper than FAL",
      },
      replicate: {
        rate: (params) => {
          const duration = "duration" in params && params.duration === 10 ? 10 : 6
          return duration === 10 ? 0.25 : 0.15
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Replicate: $0.15 (6s) or $0.25 (10s)",
      },
      fal: { rate: 0.045, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.045/sec" },
    },
  },
  // Minimax Hailuo 02 Pro - Kie.ai only, 1080p, fixed 6s, T2V + I2V
  [MinimaxVideoGenerationModelType.HAILUO_02_PRO]: {
    rate: 0.285,
    unit: EPricingUnit.PER_REQUEST,
    description: "Minimax Hailuo 02 Pro (Kie.ai): $0.285/video (1080p, 6s fixed)",
    providerCosts: {
      "kie-ai": { rate: 0.285, unit: EPricingUnit.PER_REQUEST, description: "Kie.ai: $0.285/video (1080p)" },
    },
  },
  // Minimax Hailuo 2.3 I2V Pro - Kie.ai only, 1080p, high-fidelity human motion
  [MinimaxVideoGenerationModelType.HAILUO_2_3_I2V_PRO]: {
    rate: (params) => {
      const duration = "duration" in params && params.duration === 10 ? 10 : 6
      return duration === 10 ? 0.45 : 0.225
    },
    unit: EPricingUnit.PER_REQUEST,
    description: "Minimax Hailuo 2.3 I2V Pro (Kie.ai): $0.225 (6s) / $0.45 (10s) — 1080p pro quality",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const duration = "duration" in params && params.duration === 10 ? 10 : 6
          return duration === 10 ? 0.45 : 0.225
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: $0.225 (6s) / $0.45 (10s) — 1080p pro quality",
      },
    },
  },
  // Minimax Hailuo 2.3 I2V Standard - Kie.ai only, 768p/1080p, budget I2V, 46-55% cheaper than FAL
  [MinimaxVideoGenerationModelType.HAILUO_2_3_I2V_STD]: {
    rate: (params) => {
      const duration = "duration" in params && params.duration === 10 ? 10 : 6
      const resolution = "resolution" in params ? String(params.resolution) : "768P"
      if (duration === 10) return 0.25 // 10s (768p or 1080p same price)
      return resolution === "1080P" ? 0.25 : 0.15 // 6s: $0.25 (1080p) or $0.15 (768p)
    },
    unit: EPricingUnit.PER_REQUEST,
    description:
      "Minimax Hailuo 2.3 I2V Std (Kie.ai): $0.15 (6s 768p) / $0.25 (10s or 6s 1080p) — 46-55% cheaper than FAL",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const duration = "duration" in params && params.duration === 10 ? 10 : 6
          const resolution = "resolution" in params ? String(params.resolution) : "768P"
          if (duration === 10) return 0.25
          return resolution === "1080P" ? 0.25 : 0.15
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: $0.15 (6s 768p) / $0.25 (10s or 6s 1080p)",
      },
    },
  },
  // Minimax Hailuo 02 Fast - budget-friendly fast video
  [MinimaxVideoGenerationModelType.HAILUO_02_FAST]: {
    rate: (params) => {
      const duration = "duration" in params && params.duration === 10 ? 10 : 6
      return duration === 10 ? 0.15 : 0.1
    },
    unit: EPricingUnit.PER_REQUEST,
    description: "Minimax Hailuo 02 Fast: $0.10 (6s) or $0.15 (10s) per video (512p only)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const duration = "duration" in params && params.duration === 10 ? 10 : 6
          return duration === 10 ? 0.15 : 0.1
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Replicate: $0.10 (6s) or $0.15 (10s)",
      },
    },
  },

  // Google Veo-3 Fast
  [GoogleVideoGenerationModelType.VEO_3_FAST]: {
    // No FAL endpoint — Replicate is the only routable provider ($0.15/sec, ~8s fixed = $1.20).
    // Rate held at the existing ~$1.50 flat (margin above the floor); product decision to keep
    // the existing margin rather than reprice to Replicate's per-second cost.
    rate: 1.5,
    unit: EPricingUnit.PER_REQUEST,
    description: "Google Veo-3 Fast: ~$1.50 per video (faster/cheaper Veo 3 with audio)",
    providerCosts: {
      // Schema has no generate_audio toggle (Veo-3 Fast always renders audio) → audio-tier rate.
      replicate: { rate: 0.15, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.15/sec (with audio)" },
    },
  },
  // Google Veo-3.1 Fast
  [GoogleVideoGenerationModelType.VEO_3_1_FAST]: {
    // No FAL endpoint — Replicate is the only routable provider. Audio tier held at the
    // existing $0.20/sec (~37% margin over Replicate's $0.15/sec); no-audio tier stays at
    // $0.10/sec. Product decision to keep the existing audio margin rather than reprice down.
    rate: (params) => {
      const generateAudio = "generate_audio" in params ? params.generate_audio : true
      return generateAudio ? 0.2 : 0.1
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Google Veo-3.1 Fast: $0.20/sec (with audio) or $0.10/sec (without audio)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const generateAudio = "generate_audio" in params ? params.generate_audio : true
          return generateAudio ? 0.15 : 0.1
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.15/sec (with audio) or $0.10/sec (without audio)",
      },
    },
  },

  // Google models
  [GoogleVideoGenerationModelType.VEO_2]: {
    rate: 0.5,
    unit: EPricingUnit.PER_SECOND,
    description: "Google Veo-2: estimated $0.50 per second",
    providerCosts: {
      replicate: { rate: 0.5, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.50/sec" },
      fal: { rate: 0.5, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.50/sec" },
    },
  },
  [GoogleVideoGenerationModelType.VEO_3]: {
    rate: 6.0,
    unit: EPricingUnit.PER_REQUEST,
    description: "Google Veo-3: $6 per 8-second video",
    providerCosts: {
      replicate: { rate: 6.0, unit: EPricingUnit.PER_REQUEST, description: "Replicate: $6/video" },
      fal: { rate: 6.0, unit: EPricingUnit.PER_REQUEST, description: "FAL: $6/video" },
    },
  },
  [GoogleVideoGenerationModelType.VEO_3_1]: {
    rate: (params) => {
      const generateAudio = "generate_audio" in params ? params.generate_audio : true
      // $0.40/sec with audio (25 seconds for $10), $0.20/sec without audio (50 seconds for $10)
      return generateAudio ? 0.4 : 0.2
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Google Veo-3.1: $0.40/sec (with audio) or $0.20/sec (without audio)",
    providerCosts: {
      fal: {
        rate: (params) => {
          const generateAudio = "generate_audio" in params ? params.generate_audio : true
          return generateAudio ? 0.4 : 0.2
        },
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.40/sec (audio) or $0.20/sec (no audio)",
      },
      replicate: {
        rate: (params) => {
          const generateAudio = "generate_audio" in params ? params.generate_audio : true
          return generateAudio ? 0.4 : 0.2
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.40/sec (audio) or $0.20/sec (no audio)",
      },
      "kie-ai": {
        rate: 0,
        unit: EPricingUnit.PER_REQUEST,
        description: `Kie.ai: $${KIEAI_VEO_PRICING.veo3_fast} (fast) or $${KIEAI_VEO_PRICING.veo3} (standard)`,
        calculateCost: (_rate, params) => {
          // Kie.ai Veo 3.1 uses same pricing tiers as Veo 3
          const mode = "mode" in params ? String(params.mode) : "standard"
          return mode === "fast" ? KIEAI_VEO_PRICING.veo3_fast : KIEAI_VEO_PRICING.veo3
        },
      },
    },
  },
  [GoogleVideoGenerationModelType.VEO_3_1_LITE]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      return resolution === "1080p" ? 0.08 : 0.05
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Google Veo-3.1 Lite: $0.05/sec (720p) or $0.08/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          return resolution === "1080p" ? 0.08 : 0.05
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.05/sec (720p) or $0.08/sec (1080p)",
      },
    },
  },

  // Seedance - resolution-based pricing
  [SeedanceVideoGenerationModelType.SEEDANCE_1_LITE]: {
    // No FAL endpoint — Replicate is the only routable provider. Rate held at ~50% margin
    // over Replicate ($0.018 480p / $0.072 1080p); product decision to keep the existing margin.
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "480p"
      return resolution === "1080p" ? 0.144 : 0.036
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Seedance-1-Lite: $0.036/sec (480p) or $0.144/sec (1080p)",
    providerCosts: {
      // Schema exposes only 480p/1080p (no 720p), so tier on those two.
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "480p"
          return resolution === "1080p" ? 0.072 : 0.018
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.018/sec (480p) or $0.072/sec (1080p)",
      },
    },
  },
  [SeedanceVideoGenerationModelType.SEEDANCE_1_PRO]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "480p"
      return resolution === "1080p" ? 0.15 : 0.03
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Seedance-1-Pro: $0.03/sec (480p) or $0.15/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "480p"
          return resolution === "1080p" ? 0.15 : 0.03
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.03/sec (480p) or $0.15/sec (1080p)",
      },
    },
  },
  [SeedanceVideoGenerationModelType.SEEDANCE_1_PRO_FAST]: {
    // No FAL endpoint — Replicate is the only routable provider. Rate held at the existing
    // ~33% margin over Replicate ($0.015 480p / $0.06 1080p); product decision to keep it.
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "480p"
      return resolution === "1080p" ? 0.08 : 0.02
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Seedance-1-Pro-Fast: $0.02/sec (480p) or $0.08/sec (1080p)",
    providerCosts: {
      // Schema exposes only 480p/1080p (no 720p), so tier on those two.
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "480p"
          return resolution === "1080p" ? 0.06 : 0.015
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.015/sec (480p) or $0.06/sec (1080p)",
      },
    },
  },
  [SeedanceVideoGenerationModelType.SEEDANCE_1_5_PRO]: {
    rate: (params) => {
      const generateAudio = "generate_audio" in params ? params.generate_audio : true
      // $0.052/sec with audio, $0.026/sec without
      return generateAudio ? 0.052 : 0.026
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Seedance-1.5-Pro: $0.052/sec (with audio) or $0.026/sec (without audio)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const generateAudio = "generate_audio" in params ? params.generate_audio : true
          return generateAudio ? 0.052 : 0.026
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.052/sec (audio) or $0.026/sec (no audio)",
      },
    },
  },

  // Seedance 2.0 - resolution + video-reference-based pricing via Kie AI
  [SeedanceVideoGenerationModelType.SEEDANCE_2]: {
    // Priced at FAL, which is the dearest routable provider in every scenario the schema
    // allows. Expressed as a max over the routable providers so it stays correct if any of
    // them reprices. The old rate tracked Kie.ai — the cheapest of the four and, as of the
    // dev-only routing change, not a production route at all.
    // "ceiling" basis: the customer price assumes the providers bill their maximum reference
    // input, so a long reference clip can never cost more than we charged.
    rate: (params) =>
      Math.max(
        seedance2FalRate(params, "ceiling"),
        seedance2ReplicateRate(params),
        seedance2WavespeedRate(params, "ceiling"),
      ),
    unit: EPricingUnit.PER_SECOND,
    description:
      "Seedance 2.0: FAL's rate — $0.1345/sec (480p) or $0.3034/sec (720p); with a video reference " +
      "$0.0807/$0.1814 per billed second, scaled for the reference's billed input seconds",
    providerCosts: {
      // Schema exposes only 480p/720p (no 1080p/4K), so tier on those two + video-ref presence.
      "kie-ai": {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          const hasVideoRef =
            "reference_video_urls" in params &&
            Array.isArray(params.reference_video_urls) &&
            params.reference_video_urls.length > 0
          if (resolution === "480p") return hasVideoRef ? 0.057 : 0.095
          return hasVideoRef ? 0.125 : 0.205 // 720p
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Kie.ai: no-ref 480p $0.095 / 720p $0.205; with video-ref 480p $0.057 / 720p $0.125",
      },
      replicate: {
        rate: seedance2ReplicateRate,
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: non-video-in 480p $0.08 / 720p $0.18; video-in 480p $0.10 / 720p $0.22",
      },
      wavespeed: {
        // WaveSpeed bills reference-video input seconds at the same per-second rate (clamped
        // 2-15s), so with-ref rates are scaled by the estimated input/output multiplier. The list
        // rate is then discounted by the catalog's `discount_rate: 90` — see
        // SEEDANCE_2_WAVESPEED_DISCOUNT for why that is applied here and not in the shared function.
        rate: (params) => seedance2WavespeedRate(params) * SEEDANCE_2_WAVESPEED_DISCOUNT,
        unit: EPricingUnit.PER_SECOND,
        description:
          "WaveSpeed: list non-video-in 480p $0.12 / 720p $0.24; video-in 480p $0.075 / 720p $0.15 " +
          "per billed second (input + output), less the catalog's 10% account discount",
      },
      fal: {
        // Token-based ($0.014/1k tokens, tokens = h*w*24*sec/1024); video inputs get a 0.6x
        // multiplier and input video seconds are billed too, so with-ref rates are scaled by
        // the estimated input/output multiplier.
        rate: seedance2FalRate,
        unit: EPricingUnit.PER_SECOND,
        description:
          "FAL: non-video-in 480p $0.1345 / 720p $0.3034; video-in 480p $0.0807 / 720p $0.1814 per billed second (input + output)",
      },
    },
  },
  [SeedanceVideoGenerationModelType.SEEDANCE_2_FAST]: {
    // FAL is dearest in every scenario EXCEPT 480p-with-video-reference, where WaveSpeed's
    // $0.065 base edges FAL's $0.0645 — so pricing at FAL alone would miss the floor there.
    // The max over routable providers covers both cases without a special case.
    // "ceiling" basis — see SEEDANCE_2 above.
    rate: (params) =>
      Math.max(
        seedance2FastFalRate(params, "ceiling"),
        seedance2FastReplicateRate(params),
        seedance2FastWavespeedRate(params, "ceiling"),
      ),
    unit: EPricingUnit.PER_SECOND,
    description:
      "Seedance 2.0 Fast: FAL's rate — $0.1076/sec (480p) or $0.2419/sec (720p); with a video reference " +
      "$0.0645/$0.14515 per billed second (480p floors on WaveSpeed's $0.065), scaled for billed input seconds",
    providerCosts: {
      // Schema exposes only 480p/720p, so tier on those two + video-ref presence.
      "kie-ai": {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          const hasVideoRef =
            "reference_video_urls" in params &&
            Array.isArray(params.reference_video_urls) &&
            params.reference_video_urls.length > 0
          if (resolution === "480p") return hasVideoRef ? 0.045 : 0.0775
          return hasVideoRef ? 0.1 : 0.165 // 720p
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Kie.ai: no-ref 480p $0.0775 / 720p $0.165; with video-ref 480p $0.045 / 720p $0.10",
      },
      replicate: {
        rate: seedance2FastReplicateRate,
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: non-video-in 480p $0.07 / 720p $0.15; video-in 480p $0.08 / 720p $0.17",
      },
      wavespeed: {
        // Reference-video input seconds (clamped 2-15s) are billed at the same rate, so
        // with-ref rates are scaled by the estimated input/output multiplier.
        rate: seedance2FastWavespeedRate,
        unit: EPricingUnit.PER_SECOND,
        description:
          "WaveSpeed: non-video-in 480p $0.10 / 720p $0.20; video-in 480p $0.065 / 720p $0.13 per billed second (input + output)",
      },
      fal: {
        // Token-based ($0.0112/1k tokens); video inputs get a 0.6x multiplier and input video
        // seconds are billed too, so with-ref rates are scaled by the estimated input/output multiplier.
        rate: seedance2FastFalRate,
        unit: EPricingUnit.PER_SECOND,
        description:
          "FAL: non-video-in 480p $0.1076 / 720p $0.2419; video-in 480p $0.0645 / 720p $0.14515 per billed second (input + output)",
      },
    },
  },
  [SeedanceVideoGenerationModelType.SEEDANCE_2_MINI]: {
    // FAL is the dearest production-routable provider for no-video-input requests; reference
    // requests retain the conservative FAL input-second ceiling. Kie.ai is dev-only and never
    // sizes the consumer rate.
    // "ceiling" basis — see SEEDANCE_2 above.
    rate: (params) =>
      Math.max(
        seedance2MiniFalRate(params, "ceiling"),
        seedance2MiniReplicateRate(params),
        seedance2MiniWavespeedRate(params),
      ),
    unit: EPricingUnit.PER_SECOND,
    description:
      "Seedance 2.0 Mini: $0.0721/sec (480p) or $0.1547/sec (720p), floored on FAL; with a reference " +
      "video FAL additionally bills the reference's input seconds at 0.6x",
    providerCosts: {
      "kie-ai": {
        rate: seedance2MiniKieRate,
        unit: EPricingUnit.PER_SECOND,
        description: "Kie.ai: non-video-in 480p $0.0475 / 720p $0.1025; video-in 480p $0.03 / 720p $0.0625",
      },
      replicate: {
        rate: seedance2MiniReplicateRate,
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: non-video-in 480p $0.04 / 720p $0.09; video-in 480p $0.05 / 720p $0.11",
      },
      wavespeed: {
        // Flat by resolution — reference presence does not change WaveSpeed's mini rate.
        rate: seedance2MiniWavespeedRate,
        unit: EPricingUnit.PER_SECOND,
        description: "WaveSpeed: 480p $0.06 / 720p $0.12",
      },
      fal: {
        // Token-based ($0.007/1k tokens); reference-video input seconds are billed at
        // SEEDANCE_2_MINI_FAL_REF_INPUT_FACTOR x the output rate.
        rate: seedance2MiniFalRate,
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: 480p $0.0721 / 720p $0.1547; reference-video input seconds billed at 0.6x",
      },
    },
  },

  [SeedanceVideoGenerationModelType.SEEDANCE_2_5]: {
    // Priced at the DEAREST routable provider per tier. Which provider that is FLIPS:
    // without video references replicate is cheaper (720p $0.2312 vs fal $0.4730), with them
    // replicate is far dearer (720p $0.9676 vs fal $0.2838). A scalar rate cannot express that,
    // and sizing to either provider alone loses money the moment the router picks the other.
    // "ceiling" basis on the fal side: assume the max billable reference input, so a long
    // reference clip can never cost more than we charged.
    rate: (params) => Math.max(seedance25FalRate(params, "ceiling"), seedance25ReplicateRate(params)),
    unit: EPricingUnit.PER_SECOND,
    description:
      "Seedance 2.5: dearest routable provider per tier — $0.1028-$0.2312/sec without video " +
      "references (replicate), up to $0.9676/sec with them; fal bills $0.2205-$0.4730/sec and " +
      "discounts video-reference runs to 0.6x while also billing input seconds",
    providerCosts: {
      replicate: {
        rate: seedance25ReplicateRate,
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: non-video-in 480p $0.1028 / 720p $0.2312; video-in 480p $0.4304 / 720p $0.9676",
      },
      fal: {
        // Token-based ($0.0214/1k tokens, tokens = h*w*24*sec/1024). Video references scale the
        // whole price by 0.6 but add billed input seconds (combined cap 30.2s).
        rate: seedance25FalRate,
        unit: EPricingUnit.PER_SECOND,
        description:
          "FAL: 480p $0.2205 / 720p $0.4730; with video references 0.6x the rate and input seconds billed too",
      },
    },
  },

  [BflVideoGenerationModelType.FLUX_3_VIDEO]: {
    // fal and replicate publish the IDENTICAL per-second rate for the integrated t2v/i2v variant
    // ($0.17 @720p, $0.29 @1080p — fal llms.txt and replicate's billingConfig, both 2026-08-11),
    // so the "dearest routable provider" is unambiguous and no max() is needed here.
    // Replicate also prices a draft variant ($0.06/s) and v2v ($0.41-$0.53/s); neither is
    // integrated, so neither belongs in this rate.
    rate: (params) => (("resolution" in params ? params.resolution : "720p") === "1080p" ? 0.29 : 0.17),
    unit: EPricingUnit.PER_SECOND,
    description: "Flux 3 video: $0.17/sec (720p) or $0.29/sec (1080p)",
    providerCosts: {
      fal: {
        rate: (params) => (("resolution" in params ? params.resolution : "720p") === "1080p" ? 0.29 : 0.17),
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.17/sec (720p) / $0.29/sec (1080p)",
      },
      replicate: {
        rate: (params) => (("resolution" in params ? params.resolution : "720p") === "1080p" ? 0.29 : 0.17),
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.17/sec (720p) / $0.29/sec (1080p)",
      },
    },
  },

  // Wavespeed WAN 2.1 T2V models (Replicate)
  [WavespeedVideoGenerationModelType.WAN_2_1_T2V_480P]: {
    // No FAL endpoint — Replicate is the only routable provider. Rate held at ~50% margin
    // over Replicate's $0.07/sec; product decision to keep the existing margin.
    rate: 0.14,
    unit: EPricingUnit.PER_SECOND,
    description: "WaveSpeed Wan 2.1 T2V 480p: $0.14 per second",
    providerCosts: {
      replicate: { rate: 0.07, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.07/sec (flat)" },
    },
  },
  [WavespeedVideoGenerationModelType.WAN_2_1_T2V_720P_REPLICATE]: {
    // No FAL endpoint — Replicate is the only routable provider. Rate held at ~50% margin
    // over Replicate's $0.24/sec; product decision to keep the existing margin.
    rate: 0.48,
    unit: EPricingUnit.PER_SECOND,
    description: "WaveSpeed Wan 2.1 T2V 720p (Replicate): $0.48 per second",
    providerCosts: {
      replicate: { rate: 0.24, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.24/sec (flat)" },
    },
  },

  // Wan 2.1 T2V 720p — WaveSpeed Direct API only (per-video: $0.30 base, $0.45 for extended length).
  // Schema exposes num_frames (not duration/resolution), so tier "long" on num_frames > 81.
  [WavespeedVideoGenerationModelType.WAN_2_1_T2V_720P]: {
    // No FAL or Replicate endpoint — WaveSpeed is the only routable provider. Rate held at
    // ~50% margin over WaveSpeed Direct ($0.30 base / $0.45 long); product decision to keep it.
    rate: (params) => {
      const frames = "num_frames" in params && typeof params.num_frames === "number" ? params.num_frames : 81
      return frames > 81 ? 0.9 : 0.6
    },
    unit: EPricingUnit.PER_REQUEST,
    description: "WaveSpeed Wan 2.1 T2V 720p (Direct): $0.60/video (base) or $0.90/video (extended length)",
    providerCosts: {
      wavespeed: {
        // WaveSpeed `wavespeed-ai/wan-2.1/t2v-720p`: `(duration = 10) ? base_price * 1.5 : 300000`,
        // base_price $0.30. Ledger-verified 1.00x across 3 rows — values unchanged, shape extracted
        // into the shared helper so the two i2v siblings cannot drift from it again (VM-1089).
        rate: wavespeedFlatRate(0.3, 0.45),
        unit: EPricingUnit.PER_REQUEST,
        description: "WaveSpeed: $0.30/video (base) or $0.45/video (extended length)",
      },
    },
  },

  // Wan 2.2 I2V A14B - full-quality i2v model
  [WavespeedVideoGenerationModelType.WAN_2_2_I2V_A14B]: {
    // No FAL endpoint — priced at Replicate, the only routable provider. Replicate bills a
    // FLAT price per video, so a per-second consumer rate cannot track it: it undercharged
    // ~5x at 30fps (81 frames = 2.7s) while overcharging at 5fps. Match the provider's shape
    // so the 20% margin holds at every frame/fps combination.
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      return resolution === "480p" ? 0.4 : 1.0
    },
    unit: EPricingUnit.PER_REQUEST,
    description: "Wan 2.2 I2V A14B: $0.40/video (480p) or $1.00/video (720p) — flat, matching Replicate",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          return resolution === "480p" ? 0.4 : 1.0
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Replicate: $0.40 (480p) or $1.00 (720p) per video",
      },
    },
  },

  // Wavespeed - frame-based pricing calculated as per-second.
  //
  // NOTE the split shape: Replicate genuinely bills these per second, WaveSpeed bills a FLAT price
  // per request. The `wavespeed` slot used to carry Replicate's per-second rate verbatim (copied by
  // the 2026-04-01 "provider-level pricing splits" commit, never researched), which over-recorded
  // every WaveSpeed-routed wan-2.1 i2v job by ~3.6-4.2x. Consumer `rate` is deliberately untouched
  // here — repricing is VM-1089 C5.
  [WavespeedVideoGenerationModelType.WAN_2_1_I2V_720P]: {
    rate: 0.25,
    unit: EPricingUnit.PER_SECOND,
    description: "720p: estimated $0.25 per second (frame-based)",
    providerCosts: {
      replicate: { rate: 0.25, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.25/sec" },
      wavespeed: {
        // WaveSpeed `wavespeed-ai/wan-2.1/i2v-720p`: `(duration = 10) ? base_price * 1.5 : 300000`,
        // base_price $0.30 — a FLAT per-request price, not a per-second rate.
        // Ledger (VM-1089, 2026-08-19): 2 rows billed $0.60 total against $2.5313 recorded.
        rate: wavespeedFlatRate(0.3, 0.45),
        unit: EPricingUnit.PER_REQUEST,
        description: "WaveSpeed: $0.30/video (base) or $0.45/video (extended length)",
      },
    },
  },
  [WavespeedVideoGenerationModelType.WAN_2_1_I2V_480P]: {
    rate: 0.09,
    unit: EPricingUnit.PER_SECOND,
    description: "480p: estimated $0.09 per second (frame-based)",
    providerCosts: {
      replicate: { rate: 0.09, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.09/sec" },
      wavespeed: {
        // WaveSpeed `wavespeed-ai/wan-2.1/i2v-480p-ultra-fast`:
        // `(duration = 10) ? base_price * 1.5 : 125000`, base_price $0.125 — FLAT per request.
        // Ledger (VM-1089): job b31d0bfc... (num_frames 81 @ 16fps) billed $0.125 vs $0.4556 recorded.
        rate: wavespeedFlatRate(0.125, 0.1875),
        unit: EPricingUnit.PER_REQUEST,
        description: "WaveSpeed: $0.125/video (base) or $0.1875/video (extended length)",
      },
    },
  },

  // Wan 2.5 models - resolution-based pricing
  [WavespeedVideoGenerationModelType.WAN_2_5_T2V]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      if (resolution === "480p") return 0.05
      if (resolution === "1080p") return 0.15
      return 0.1 // 720p default
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 2.5 T2V: $0.05/sec (480p), $0.10/sec (720p), $0.15/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          if (resolution === "480p") return 0.05
          if (resolution === "1080p") return 0.15
          return 0.1
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.05-0.15/sec",
      },
      wavespeed: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          if (resolution === "480p") return 0.05
          if (resolution === "1080p") return 0.15
          return 0.1
        },
        unit: EPricingUnit.PER_SECOND,
        description: "WaveSpeed: $0.05-0.15/sec",
      },
    },
  },
  [WavespeedVideoGenerationModelType.WAN_2_5_I2V]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      if (resolution === "480p") return 0.05
      if (resolution === "1080p") return 0.15
      return 0.1 // 720p default
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 2.5 I2V: $0.05/sec (480p), $0.10/sec (720p), $0.15/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          if (resolution === "480p") return 0.05
          if (resolution === "1080p") return 0.15
          return 0.1
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.05-0.15/sec",
      },
      wavespeed: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          if (resolution === "480p") return 0.05
          if (resolution === "1080p") return 0.15
          return 0.1
        },
        unit: EPricingUnit.PER_SECOND,
        description: "WaveSpeed: $0.05-0.15/sec",
      },
    },
  },
  [WavespeedVideoGenerationModelType.WAN_2_5_T2V_FAST]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      return resolution === "1080p" ? 0.102 : 0.068 // 720p default
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 2.5 T2V Fast: $0.068/sec (720p), $0.102/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          return resolution === "1080p" ? 0.102 : 0.068
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.068-0.102/sec",
      },
      wavespeed: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          return resolution === "1080p" ? 0.102 : 0.068
        },
        unit: EPricingUnit.PER_SECOND,
        description: "WaveSpeed: $0.068-0.102/sec",
      },
    },
  },
  [WavespeedVideoGenerationModelType.WAN_2_5_I2V_FAST]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      return resolution === "1080p" ? 0.102 : 0.068 // 720p default
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 2.5 I2V Fast: $0.068/sec (720p), $0.102/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          return resolution === "1080p" ? 0.102 : 0.068
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.068-0.102/sec",
      },
      wavespeed: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          return resolution === "1080p" ? 0.102 : 0.068
        },
        unit: EPricingUnit.PER_SECOND,
        description: "WaveSpeed: $0.068-0.102/sec",
      },
    },
  },

  // Wan 2.2 fast variants (Pruna AI optimized)
  [WavespeedVideoGenerationModelType.WAN_2_2_I2V_FAST]: {
    // Flat per video on Replicate, the only routable provider — see WAN_2_2_I2V_A14B for why
    // a per-second consumer rate cannot track a flat provider cost.
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      return resolution === "480p" ? 0.055 : 0.125
    },
    unit: EPricingUnit.PER_REQUEST,
    description: "Wan 2.2 I2V Fast: $0.055/video (480p) or $0.125/video (720p) - Pruna AI optimized, flat per video",
    providerCosts: {
      // Midpoints of the Replicate per-video ranges.
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          return resolution === "480p" ? 0.055 : 0.125
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Replicate: $0.055 (480p) or $0.125 (720p) per video",
      },
    },
  },
  [WavespeedVideoGenerationModelType.WAN_2_2_T2V_FAST]: {
    // Flat per video on Replicate, the only routable provider — see WAN_2_2_I2V_A14B.
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "480p"
      return resolution === "720p" ? 0.1 : 0.05
    },
    unit: EPricingUnit.PER_REQUEST,
    description: "Wan 2.2 T2V Fast: $0.05/video (480p) or $0.10/video (720p) - Pruna AI optimized, flat per video",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "480p"
          return resolution === "720p" ? 0.1 : 0.05
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Replicate: $0.05 (480p) or $0.10 (720p) per video",
      },
    },
  },

  // Wan 2.2 models - CHEAPEST video model on Replicate (flat rate per video)
  [WavespeedVideoGenerationModelType.WAN_2_2_5B_FAST]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      // Flat rate per video: $0.0125 (480p), $0.025 (720p)
      // Convert to per-second rate based on typical ~3.38s video (81 frames at 24fps)
      return resolution === "480p" ? 0.0125 / 3.38 : 0.025 / 3.38
    },
    unit: EPricingUnit.PER_SECOND,
    calculateCost: (_rate, params) => {
      // Flat rate pricing - ignore duration, just use resolution
      const resolution = "resolution" in params ? params.resolution : "720p"
      return resolution === "480p" ? 0.0125 : 0.025
    },
    description: "Wan 2.2 5B Fast: $0.0125/video (480p), $0.025/video (720p) - CHEAPEST",
    providerCosts: {
      replicate: {
        rate: 0,
        unit: EPricingUnit.PER_REQUEST,
        description: "Replicate: $0.0125 (480p) or $0.025 (720p) flat",
        calculateCost: (_rate, params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          return resolution === "480p" ? 0.0125 : 0.025
        },
      },
    },
  },

  // Wan 2.7 models - newest Alibaba Wan series, 1080p, audio sync (Replicate + Kie.ai)
  [WavespeedVideoGenerationModelType.WAN_2_7_T2V]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      if (resolution === "1080p") return 0.15
      return 0.1 // 720p default
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 2.7 T2V: $0.10/sec (720p) or $0.15/sec (1080p), with audio",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          if (resolution === "1080p") return 0.15
          return 0.1
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.10/sec (720p), $0.15/sec (1080p)",
      },
      "kie-ai": {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          if (resolution === "1080p") return 0.12
          return 0.08
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Kie.ai: ~$0.08/sec (720p), ~$0.12/sec (1080p) — ~20% cheaper",
      },
    },
  },
  [WavespeedVideoGenerationModelType.WAN_2_7_I2V]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      if (resolution === "1080p") return 0.15
      return 0.1 // 720p default
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 2.7 I2V: $0.10/sec (720p) or $0.15/sec (1080p), first/last frame + audio",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          if (resolution === "1080p") return 0.15
          return 0.1
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.10/sec (720p), $0.15/sec (1080p)",
      },
      "kie-ai": {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          if (resolution === "1080p") return 0.12
          return 0.08
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Kie.ai: ~$0.08/sec (720p), ~$0.12/sec (1080p) — ~20% cheaper",
      },
    },
  },

  // Wan 3.0 — FAL is the dearest production route and sets the consumer floor.
  [WavespeedVideoGenerationModelType.WAN_3_0_T2V]: {
    rate: (params) => wan3ResolutionRate(params, { "480p": 0.05, "720p": 0.1, "1080p": 0.2 }),
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 3.0 T2V: $0.05/sec (480p), $0.10/sec (720p), $0.20/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => wan3ResolutionRate(params, { "480p": 0.025, "720p": 0.05, "1080p": 0.1 }),
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.025/sec (480p), $0.05/sec (720p), $0.10/sec (1080p)",
      },
      fal: {
        rate: (params) => wan3ResolutionRate(params, { "480p": 0.05, "720p": 0.1, "1080p": 0.2 }),
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.05/sec (480p), $0.10/sec (720p), $0.20/sec (1080p)",
      },
    },
  },
  [WavespeedVideoGenerationModelType.WAN_3_0_I2V]: {
    rate: (params) => wan3ResolutionRate(params, { "480p": 0.05, "720p": 0.1, "1080p": 0.2 }),
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 3.0 I2V: $0.05/sec (480p), $0.10/sec (720p), $0.20/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => wan3ResolutionRate(params, { "480p": 0.025, "720p": 0.05, "1080p": 0.1 }),
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.025/sec (480p), $0.05/sec (720p), $0.10/sec (1080p)",
      },
      fal: {
        rate: (params) => wan3ResolutionRate(params, { "480p": 0.05, "720p": 0.1, "1080p": 0.2 }),
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.05/sec (480p), $0.10/sec (720p), $0.20/sec (1080p)",
      },
    },
  },
  [WavespeedVideoGenerationModelType.WAN_3_0_PRIME_T2V]: {
    rate: (params) => wan3ResolutionRate(params, { "480p": 0.068, "720p": 0.14, "1080p": 0.28 }),
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 3.0 Prime T2V: $0.068/sec (480p), $0.14/sec (720p), $0.28/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => wan3ResolutionRate(params, { "480p": 0.068, "720p": 0.14, "1080p": 0.28 }),
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.068/sec (480p), $0.14/sec (720p), $0.28/sec (1080p)",
      },
      fal: {
        rate: (params) => wan3ResolutionRate(params, { "480p": 0.068, "720p": 0.14, "1080p": 0.28 }),
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.068/sec (480p), $0.14/sec (720p), $0.28/sec (1080p)",
      },
    },
  },
  [WavespeedVideoGenerationModelType.WAN_3_0_PRIME_I2V]: {
    rate: (params) => wan3ResolutionRate(params, { "480p": 0.068, "720p": 0.14, "1080p": 0.28 }),
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 3.0 Prime I2V: $0.068/sec (480p), $0.14/sec (720p), $0.28/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => wan3ResolutionRate(params, { "480p": 0.068, "720p": 0.14, "1080p": 0.28 }),
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.068/sec (480p), $0.14/sec (720p), $0.28/sec (1080p)",
      },
      fal: {
        rate: (params) => wan3ResolutionRate(params, { "480p": 0.068, "720p": 0.14, "1080p": 0.28 }),
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.068/sec (480p), $0.14/sec (720p), $0.28/sec (1080p)",
      },
    },
  },

  // HunyuanVideo Fast - Tencent's high-quality T2V at 720p via WaveSpeed AI
  [HunyuanVideoGenerationModelType.HUNYUAN_VIDEO_FAST]: {
    rate: 0.1,
    unit: EPricingUnit.PER_SECOND,
    description: "HunyuanVideo Fast: $0.10 per second (720p, state-of-the-art motion quality)",
  },

  // OpenAI models
  [OpenAIVideoGenerationModelType.SORA_2]: {
    // Was $0.035 PER_REQUEST — a stray divide of Kie.ai's $0.175 flat rate that sold every
    // Sora 2 clip at roughly -1000% to -3300% margin against the providers that actually
    // serve it. FAL and Replicate both bill $0.10/SEC and the schema allows 4/8/12s, so a
    // flat per-request rate cannot track them (pinning it to the 12s worst case would charge
    // $1.20 for a 4s clip). Per-second at FAL's own $0.10/sec is exactly 20% at every duration.
    rate: 0.1,
    unit: EPricingUnit.PER_SECOND,
    description: "OpenAI Sora 2: $0.10/sec (FAL, matched by Replicate); Kie.ai's $0.175/video is dev-only",
    providerCosts: {
      replicate: { rate: 0.1, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.10/sec" },
      fal: { rate: 0.1, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.10/sec" },
      "kie-ai": {
        rate: 0.175,
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: $0.175/video (flat rate, 88% cheaper than FAL)",
      },
    },
  },
  [OpenAIVideoGenerationModelType.SORA_2_PRO]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "standard"
      // $0.30/sec standard, $0.50/sec high quality
      return resolution === "high" ? 0.5 : 0.3
    },
    unit: EPricingUnit.PER_SECOND,
    description: "OpenAI Sora 2 Pro: $0.30/sec (standard) or $0.50/sec (high quality)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "standard"
          return resolution === "high" ? 0.5 : 0.3
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.30-0.50/sec",
      },
      fal: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "standard"
          return resolution === "high" ? 0.5 : 0.3
        },
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.30-0.50/sec",
      },
    },
  },

  // Sora 2 Pro Storyboard (Kie.ai only — multi-scene storyboard generation)
  // $0.75/video (10s), $1.35/video (15-25s)
  [OpenAIVideoGenerationModelType.SORA_2_PRO_STORYBOARD]: {
    rate: (params) => {
      const nFrames = "n_frames" in params ? params.n_frames : "10"
      return nFrames === "10" ? 0.75 : 1.35
    },
    unit: EPricingUnit.PER_REQUEST,
    description: "Sora 2 Pro Storyboard: $0.75/video (10s), $1.35/video (15-25s) — Kie.ai multi-scene generation",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const nFrames = "n_frames" in params ? params.n_frames : "10"
          return nFrames === "10" ? 0.75 : 1.35
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: $0.75/video (10s), $1.35/video (15-25s)",
      },
    },
  },

  // Pixverse models - complex pricing based on quality, duration, and motion mode
  [PixverseVideoGenerationModelType.PIXVERSE_V4_5]: {
    rate: (params) => {
      // Base prices for 5s videos:
      // 360p/540p: $0.30, 720p: $0.40, 1080p: $0.80
      // 8s videos: 2x the 5s price
      // Smooth motion: 2x normal price
      const quality = "quality" in params ? params.quality : "720p"
      const duration = "duration" in params && params.duration === 8 ? 8 : 5
      const motionMode = "motion_mode" in params ? params.motion_mode : "normal"

      let basePrice: number
      switch (quality) {
        case "360p":
        case "540p":
          basePrice = 0.3
          break
        case "1080p":
          basePrice = 0.8
          break
        case "720p":
        default:
          basePrice = 0.4
          break
      }

      // Duration multiplier (8s is 2x the 5s price)
      const durationMultiplier = duration === 8 ? 2 : 1

      // Motion mode multiplier (smooth is 2x normal)
      const motionMultiplier = motionMode === "smooth" ? 2 : 1

      // Return per-second rate for compatibility with calculatePerSecond
      return (basePrice * durationMultiplier * motionMultiplier) / duration
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Pixverse v4.5: $0.30-$0.80 (5s), doubled for 8s or smooth motion",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const quality = "quality" in params ? params.quality : "720p"
          const duration = "duration" in params && params.duration === 8 ? 8 : 5
          const motionMode = "motion_mode" in params ? params.motion_mode : "normal"
          let basePrice = 0.4
          if (quality === "360p" || quality === "540p") basePrice = 0.3
          else if (quality === "1080p") basePrice = 0.8
          const durationMultiplier = duration === 8 ? 2 : 1
          const motionMultiplier = motionMode === "smooth" ? 2 : 1
          return (basePrice * durationMultiplier * motionMultiplier) / duration
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.30-$0.80 (5s), doubled for 8s/smooth",
      },
    },
  },

  // Runway models - FASTEST high-quality video generation
  [RunwayVideoGenerationModelType.GEN4_TURBO]: {
    rate: 0.05,
    unit: EPricingUnit.PER_SECOND,
    description: "Runway Gen-4 Turbo: $0.05 per second (~25s generation, fastest high-quality model)",
    providerCosts: {
      replicate: { rate: 0.05, unit: EPricingUnit.PER_SECOND, description: "Replicate: $0.05/sec" },
    },
  },

  // Pixverse V6 - resolution-based pricing (FAL)
  [PixverseVideoGenerationModelType.PIXVERSE_V6]: {
    rate: (params) => {
      const resolution = String("resolution" in params ? params.resolution : "720p")
      const hasAudio = "sound_effect_switch" in params ? params.sound_effect_switch : false
      // Base rates without audio: 360p=$0.025, 540p=$0.035, 720p=$0.045, 1080p=$0.09
      let base = 0.045
      if (resolution === "360p") base = 0.025
      else if (resolution === "540p") base = 0.035
      else if (resolution === "1080p") base = 0.09
      // Audio adds ~$0.01-0.025 depending on resolution
      return hasAudio ? base * 1.3 : base
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Pixverse V6: $0.025-0.115/sec (resolution + audio)",
    providerCosts: {
      fal: {
        rate: (params) => {
          const resolution = String("resolution" in params ? params.resolution : "720p")
          const hasAudio = "sound_effect_switch" in params ? params.sound_effect_switch : false
          let base = 0.045
          if (resolution === "360p") base = 0.025
          else if (resolution === "540p") base = 0.035
          else if (resolution === "1080p") base = 0.09
          return hasAudio ? base * 1.3 : base
        },
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.025-0.115/sec",
      },
    },
  },

  // Luma Ray 2 - per-request with resolution/duration multipliers (FAL)
  [LumaVideoGenerationModelType.RAY_2]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "540p"
      const duration = "duration" in params ? params.duration : "5s"
      let base = 0.5 // $0.50 per 5s at 540p
      if (resolution === "720p") base *= 2
      if (resolution === "1080p") base *= 4
      if (duration === "9s") base *= 2
      return base / 5 // Convert to per-second for compatibility
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Luma Ray 2: $0.50/5s (540p), 2x for 720p, 4x for 1080p, 2x for 9s",
    providerCosts: {
      fal: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "540p"
          const duration = "duration" in params ? params.duration : "5s"
          let base = 0.5
          if (resolution === "720p") base *= 2
          if (resolution === "1080p") base *= 4
          if (duration === "9s") base *= 2
          return base / 5
        },
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.50/5s (540p), 2x/4x for higher res",
      },
    },
  },

  // Luma Ray 3.2 - WaveSpeed (flat per-VIDEO, res + length tiers) + Replicate (per-video by
  // resolution × duration). No FAL endpoint. The two routable providers price in DIFFERENT
  // SHAPES and neither dominates. Base held at $0.20/sec per the original product decision;
  // the Replicate per-video floor is KEPT so 1080p (where Replicate is dearer than any
  // per-second rate) can never sell below cost.
  //
  // VM-1089 FLOOR_FAIL: WaveSpeed's real bill is $1.00 for the 720p/5s clip we sell for $1.00,
  // and $2.00 for a 1080p/5s clip we sell for $1.20. Margin is zero-to-negative on the WaveSpeed
  // route. Recorded here, NOT fixed here — the consumer reprice is approval-gated to VM-1089 C5.
  [LumaVideoGenerationModelType.RAY_3_2]: {
    rate: 0.2,
    unit: EPricingUnit.PER_SECOND,
    description:
      "Luma Ray 3.2: $0.20/sec, floored at Replicate's per-video rate — 1080p $1.20/5s $3.70/10s. " +
      "Does NOT clear WaveSpeed's per-video bill ($1.00 at 720p/5s, $2.00 at 1080p/5s) — VM-1089 C5.",
    calculateCost: (rate, params) => Math.max(calculatePerSecond(rate, params), getReplicateRay32Cost(params)),
    providerCosts: {
      wavespeed: {
        // See `getWavespeedRay32Cost` — flat per request, res + length tiers. The old flat
        // $0.10/sec under-recorded every routed job by 2.00x. Consumer rate untouched (VM-1089 C5).
        rate: getWavespeedRay32Cost,
        unit: EPricingUnit.PER_REQUEST,
        description: "WaveSpeed: $0.50 base x (10s ? 2 : 1) x (1080p ? 4 : 720p ? 2 : 1) per video",
      },
      // Replicate bills per video (SDR; schema exposes no hdr/540p): 720p $0.30/5s, $0.90/10s;
      // 1080p $1.20/5s, $3.70/10s. Verified from replicate.com/luma/ray-3.2 pricing table.
      replicate: {
        rate: getReplicateRay32Cost,
        unit: EPricingUnit.PER_REQUEST,
        description: "Replicate: 720p $0.30/5s $0.90/10s; 1080p $1.20/5s $3.70/10s (per video, SDR)",
      },
    },
  },

  // LTX 2.3 Fast - budget video model (FAL)
  [LtxVideoGenerationModelType.LTX_2_3_FAST]: {
    rate: 0.02,
    unit: EPricingUnit.PER_SECOND,
    description: "LTX 2.3 Fast: ~$0.02/sec (budget model)",
    providerCosts: {
      fal: { rate: 0.02, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.02/sec" },
    },
  },

  // LTX 2 19B - larger, higher-quality model (FAL)
  [LtxVideoGenerationModelType.LTX_2_19B]: {
    rate: 0.0018,
    unit: EPricingUnit.PER_SECOND,
    description: "LTX 2 19B: $0.0018/sec (per megapixel)",
    providerCosts: {
      fal: { rate: 0.0018, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.0018/sec" },
    },
  },

  // LTX 2.3 22B Distilled - reference video-to-video (FAL)
  [LtxVideoGenerationModelType.LTX_2_3_22B_DISTILLED]: {
    rate: 0.0012,
    unit: EPricingUnit.PER_SECOND,
    description: "LTX 2.3 22B Distilled: $0.0012/sec (reference video-to-video)",
    providerCosts: {
      fal: { rate: 0.0012, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.0012/sec" },
    },
  },

  // LTX Video 13B Distilled - fast 13B i2v (FAL)
  [LtxVideoGenerationModelType.LTX_VIDEO_13B_DISTILLED]: {
    rate: 0.015,
    unit: EPricingUnit.PER_SECOND,
    description: "LTX Video 13B Distilled: ~$0.015/sec (fast 13B i2v)",
    providerCosts: {
      fal: { rate: 0.015, unit: EPricingUnit.PER_SECOND, description: "FAL: ~$0.015/sec" },
    },
  },

  // Pixverse C1 - cinematic quality, 1080p, audio support (FAL)
  [PixverseVideoGenerationModelType.PIXVERSE_C1]: {
    rate: (params) => {
      const quality = "quality" in params ? String(params.quality) : "720p"
      const hasAudio = "sound_effect_switch" in params ? Boolean(params.sound_effect_switch) : false
      let base = 0.045
      if (quality === "360p") base = 0.025
      else if (quality === "540p") base = 0.035
      else if (quality === "1080p") base = 0.09
      return hasAudio ? base * 1.3 : base
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Pixverse C1: $0.025-0.117/sec (resolution + audio)",
    providerCosts: {
      fal: {
        rate: (params) => {
          const quality = "quality" in params ? String(params.quality) : "720p"
          const hasAudio = "sound_effect_switch" in params ? Boolean(params.sound_effect_switch) : false
          let base = 0.045
          if (quality === "360p") base = 0.025
          else if (quality === "540p") base = 0.035
          else if (quality === "1080p") base = 0.09
          return hasAudio ? base * 1.3 : base
        },
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.025-0.117/sec",
      },
    },
  },

  // Pixverse C1 Transition - morphing/transition between two images (FAL)
  [PixverseVideoGenerationModelType.PIXVERSE_C1_TRANSITION]: {
    rate: (params) => {
      const quality = "quality" in params ? String(params.quality) : "720p"
      let base = 0.045
      if (quality === "360p") base = 0.025
      else if (quality === "540p") base = 0.035
      else if (quality === "1080p") base = 0.09
      return base
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Pixverse C1 Transition: $0.025-0.09/sec (morphing between two images)",
    providerCosts: {
      fal: {
        rate: (params) => {
          const quality = "quality" in params ? String(params.quality) : "720p"
          let base = 0.045
          if (quality === "360p") base = 0.025
          else if (quality === "540p") base = 0.035
          else if (quality === "1080p") base = 0.09
          return base
        },
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.025-0.09/sec",
      },
    },
  },

  // Vidu Q3 - reference-based video generation (FAL)
  [ViduVideoGenerationModelType.VIDU_Q3]: {
    rate: 0.05,
    unit: EPricingUnit.PER_SECOND,
    description: "Vidu Q3: ~$0.05/sec (reference-based video)",
    providerCosts: {
      fal: { rate: 0.05, unit: EPricingUnit.PER_SECOND, description: "FAL: ~$0.05/sec" },
    },
  },

  [RunwayVideoGenerationModelType.GEN4_5]: {
    rate: 0.055,
    unit: EPricingUnit.PER_SECOND,
    description: "Runway Gen-4.5: $0.055 per second (state-of-the-art motion quality)",
  },

  // Runway Aleph (Kie.ai) - flat $0.55/video
  [RunwayVideoGenerationModelType.ALEPH]: {
    rate: 0.55,
    unit: EPricingUnit.PER_REQUEST,
    description: "Runway Aleph: $0.55 per video (Kie.ai)",
    providerCosts: {
      "kie-ai": {
        rate: 0.55,
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: $0.55/video",
      },
    },
  },

  // Pixverse v5 / v5.6 models
  [PixverseVideoGenerationModelType.PIXVERSE_V5]: {
    rate: (params) => {
      const quality = "quality" in params ? params.quality : "720p"
      const duration = "duration" in params && params.duration === 8 ? 8 : 5
      let basePrice: number
      switch (quality) {
        case "360p":
        case "540p":
          basePrice = 0.3
          break
        case "1080p":
          basePrice = 0.8
          break
        case "720p":
        default:
          basePrice = 0.45
          break
      }
      return (basePrice * (duration === 8 ? 2 : 1)) / duration
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Pixverse v5: $0.30-$0.80 (5s) based on quality",
  },
  [PixverseVideoGenerationModelType.PIXVERSE_V5_6]: {
    rate: (params) => {
      const quality = "quality" in params ? params.quality : "720p"
      const duration = "duration" in params && params.duration === 8 ? 8 : 5
      let basePrice: number
      switch (quality) {
        case "360p":
        case "540p":
          basePrice = 0.35
          break
        case "1080p":
          basePrice = 0.9
          break
        case "720p":
        default:
          basePrice = 0.5
          break
      }
      return (basePrice * (duration === 8 ? 2 : 1)) / duration
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Pixverse v5.6: $0.35-$0.90 (5s) based on quality — latest with enhanced physics",
  },

  // LTX 2.3 22B Reference V2V (FAL) — non-distilled, slightly higher cost than distilled
  [LtxVideoGenerationModelType.LTX_2_3_22B_REFERENCE]: {
    rate: 0.0018,
    unit: EPricingUnit.PER_SECOND,
    description: "LTX 2.3 22B Reference V2V: $0.0018/sec (non-distilled, per megapixel)",
    providerCosts: {
      fal: { rate: 0.0018, unit: EPricingUnit.PER_SECOND, description: "FAL: $0.0018/sec" },
    },
  },

  // ===== Grok video (xAI) — priced at FAL, which is the only production-routable provider =====
  //
  // Both entries were sized to Kie.ai while these models were Kie-only. Kie is dev-only and is
  // dropped from production routing (video/registry.ts), so it never sets the price; the moment
  // the FAL route was added, every Kie-sized tier billed UNDER provider cost. Repriced to FAL's
  // published per-resolution rates so the 1.2x consumer markup is real margin rather than a loss
  // — the same failure `openai/sora-2` shipped (references/pricing-floor.md).
  //
  // FAL rates verified 2026-08-13 from the bulk catalog's pricingInfoOverride.
  //
  // Grok Video — FAL xai/grok-imagine-video/{text-to-video,image-to-video}; Kie grok-imagine/*
  [GrokVideoGenerationModelType.GROK_VIDEO]: {
    rate: (params) => grokVideoResolutionRate(params, 0.05, 0.07),
    unit: EPricingUnit.PER_SECOND,
    description: "Grok Video (xAI): $0.05/sec (480p), $0.07/sec (720p); i2v adds $0.002 for the input image",
    calculateCost: (rate, params) => grokVideoCost(rate, params, GROK_VIDEO_FAL_IMAGE_INPUT_USD),
    providerCosts: {
      fal: {
        rate: (params) => grokVideoResolutionRate(params, 0.05, 0.07),
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.05/sec (480p), $0.07/sec (720p) + $0.002 per input image",
        calculateCost: (rate, params) => grokVideoCost(rate, params, GROK_VIDEO_FAL_IMAGE_INPUT_USD),
      },
      "kie-ai": { rate: 0.015, unit: EPricingUnit.PER_SECOND, description: "Kie.ai: $0.015/sec (dev-only)" },
    },
  },

  // Grok Imagine Video 1.5 Preview — FAL xai/grok-imagine-video/v1.5/image-to-video; Kie grok-imagine-video-1.5
  // FAL also lists 1080p at $0.25/sec; the schema does not expose it, so it needs no tier here.
  [GrokVideoGenerationModelType.GROK_VIDEO_1_5]: {
    rate: (params) => grokVideoResolutionRate(params, 0.08, 0.14),
    unit: EPricingUnit.PER_SECOND,
    description: "Grok Imagine Video 1.5 Preview (xAI): $0.08/sec (480p), $0.14/sec (720p) + $0.01 for the input image",
    calculateCost: (rate, params) => grokVideoCost(rate, params, GROK_VIDEO_15_FAL_IMAGE_INPUT_USD),
    providerCosts: {
      fal: {
        rate: (params) => grokVideoResolutionRate(params, 0.08, 0.14),
        unit: EPricingUnit.PER_SECOND,
        description: "FAL: $0.08/sec (480p), $0.14/sec (720p) + $0.01 per input image",
        calculateCost: (rate, params) => grokVideoCost(rate, params, GROK_VIDEO_15_FAL_IMAGE_INPUT_USD),
      },
      "kie-ai": {
        rate: (params) => grokVideoResolutionRate(params, 0.0725, 0.125),
        unit: EPricingUnit.PER_SECOND,
        description: "Kie.ai: $0.0725/sec (480p), $0.125/sec (720p) (dev-only)",
      },
    },
  },

  // HappyHorse T2V (Kie.ai: happyhorse/text-to-video) — Alibaba model, $0.265/sec (1080p)
  [HappyHorseVideoGenerationModelType.HAPPYHORSE_T2V]: {
    rate: 0.265,
    unit: EPricingUnit.PER_SECOND,
    description: "HappyHorse T2V (Alibaba): $0.265/sec (1080p), $0.155/sec (720p)",
    providerCosts: {
      "kie-ai": { rate: 0.265, unit: EPricingUnit.PER_SECOND, description: "Kie.ai: $0.265/sec (1080p)" },
    },
  },

  // HappyHorse I2V (Kie.ai: happyhorse/image-to-video) — Alibaba model, $0.265/sec (1080p)
  [HappyHorseVideoGenerationModelType.HAPPYHORSE_I2V]: {
    rate: 0.265,
    unit: EPricingUnit.PER_SECOND,
    description: "HappyHorse I2V (Alibaba): $0.265/sec (1080p), $0.155/sec (720p)",
    providerCosts: {
      "kie-ai": { rate: 0.265, unit: EPricingUnit.PER_SECOND, description: "Kie.ai: $0.265/sec (1080p)" },
    },
  },

  // HappyHorse R2V (Kie.ai: happyhorse/reference-to-video) — Alibaba, character/style consistency
  [HappyHorseVideoGenerationModelType.HAPPYHORSE_R2V]: {
    rate: 0.265,
    unit: EPricingUnit.PER_SECOND,
    description: "HappyHorse R2V (Alibaba): $0.265/sec (1080p), $0.155/sec (720p)",
    providerCosts: {
      "kie-ai": { rate: 0.265, unit: EPricingUnit.PER_SECOND, description: "Kie.ai: $0.265/sec (1080p)" },
    },
  },

  // HappyHorse Video Edit (Kie.ai: happyhorse/video-edit) — Alibaba, video-to-video editing
  [HappyHorseVideoGenerationModelType.HAPPYHORSE_VIDEO_EDIT]: {
    rate: 0.265,
    unit: EPricingUnit.PER_SECOND,
    description: "HappyHorse VideoEdit (Alibaba): $0.265/sec (1080p), $0.155/sec (720p)",
    providerCosts: {
      "kie-ai": { rate: 0.265, unit: EPricingUnit.PER_SECOND, description: "Kie.ai: $0.265/sec (1080p)" },
    },
  },
  [HappyHorseVideoGenerationModelType.HAPPYHORSE_1_1]: {
    // 1080p priced at WaveSpeed's $0.189/sec (the dearest routable provider — its r2v tier is
    // above Replicate/FAL's $0.18), so the 20% floor holds on a WaveSpeed-routed request.
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "1080p"
      return resolution === "720p" ? 0.14 : 0.189
    },
    unit: EPricingUnit.PER_SECOND,
    description:
      "HappyHorse 1.1: $0.14/sec (720p), $0.189/sec (1080p, WaveSpeed worst case). Replicate (all modes), Kie (t2v/i2v/r2v), fal (t2v/i2v/r2v), WaveSpeed (r2v)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "1080p"
          return resolution === "720p" ? 0.14 : 0.18
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.14/sec (720p), $0.18/sec (1080p)",
      },
      // Kie.ai serves t2v/i2v/r2v (happyhorse-1-1/*), same tiered rate per mode, ~20% cheaper than fal.
      "kie-ai": {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "1080p"
          return resolution === "720p" ? 0.1125 : 0.145
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Kie.ai (t2v/i2v/r2v): $0.1125/sec (720p), $0.145/sec (1080p)",
      },
      // fal serves t2v/i2v/r2v (alibaba/happy-horse/v1.1/*), same tiered rate for every mode.
      fal: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "1080p"
          return resolution === "720p" ? 0.14 : 0.18
        },
        unit: EPricingUnit.PER_SECOND,
        description: "fal (t2v/i2v/r2v): $0.14/sec (720p), $0.18/sec (1080p)",
      },
      // WaveSpeed serves r2v only (alibaba/happyhorse-1.1/reference-to-video), per-5s billing
      // quoted as $0.70/5s (720p) and $0.945/5s (1080p) → per-second below.
      wavespeed: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "1080p"
          return resolution === "720p" ? 0.14 : 0.189
        },
        unit: EPricingUnit.PER_SECOND,
        description: "WaveSpeed (r2v): $0.14/sec (720p), $0.189/sec (1080p)",
      },
    },
  },

  // Wan 2.7 R2V (Kie.ai: wan/2-7-r2v) — reference-to-video, 20% cheaper than FAL
  [WavespeedVideoGenerationModelType.WAN_2_7_R2V]: {
    rate: 0.12,
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 2.7 R2V: $0.12/sec (1080p), $0.08/sec (720p) — 20% cheaper than FAL",
    providerCosts: {
      "kie-ai": { rate: 0.12, unit: EPricingUnit.PER_SECOND, description: "Kie.ai: $0.12/sec (1080p)" },
    },
  },

  // Wan 2.7 VideoEdit (Kie.ai: wan/2-7-videoedit) — V2V editing with prompt, $0.12/sec (1080p)
  [WavespeedVideoGenerationModelType.WAN_2_7_VIDEOEDIT]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "1080p"
      return resolution === "1080p" ? 0.12 : 0.08
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 2.7 VideoEdit: $0.12/sec (1080p), $0.08/sec (720p) — Kie.ai video-to-video editing",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "1080p"
          return resolution === "1080p" ? 0.12 : 0.08
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Kie.ai: $0.12/sec (1080p), $0.08/sec (720p)",
      },
    },
  },

  // Wan 2.6 Video-to-Video (Kie.ai only, 30% cheaper than FAL)
  // $0.35-1.575/video by duration (5/10s) and resolution (720p/1080p)
  [WavespeedVideoGenerationModelType.WAN_2_6_V2V]: {
    rate: (params) => {
      const duration = "duration" in params && typeof params.duration === "number" ? params.duration : 5
      const resolution = "resolution" in params ? params.resolution : "1080p"
      // 5s: $0.35 (720p) / $0.5225 (1080p); 10s: $0.70 (720p) / $1.0475 (1080p)
      if (resolution === "1080p") return duration >= 10 ? 1.0475 : 0.5225
      return duration >= 10 ? 0.7 : 0.35
    },
    unit: EPricingUnit.PER_REQUEST,
    description: "Wan 2.6 V2V: $0.35-1.575/video (Kie.ai, 30% off FAL) — video-to-video restyle",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const duration = "duration" in params && typeof params.duration === "number" ? params.duration : 5
          const resolution = "resolution" in params ? params.resolution : "1080p"
          if (resolution === "1080p") return duration >= 10 ? 1.0475 : 0.5225
          return duration >= 10 ? 0.7 : 0.35
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: $0.35-1.575/video",
      },
    },
  },

  // Wan 2.6 T2V — Replicate (per-sec, primary) + WaveSpeed & Kie.ai (per-video by duration+resolution).
  [WavespeedVideoGenerationModelType.WAN_2_6_T2V]: {
    // No FAL endpoint — Replicate/WaveSpeed both cost ~$0.10/$0.15 per second. Rate held at
    // ~50% margin over that ($0.20/$0.30); product decision to keep the existing margin.
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      return resolution === "1080p" ? 0.3 : 0.2
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 2.6 T2V: $0.20/sec (720p) or $0.30/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          return resolution === "1080p" ? 0.15 : 0.1
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.10/sec (720p), $0.15/sec (1080p)",
      },
      wavespeed: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          const duration = "duration" in params && typeof params.duration === "number" ? params.duration : 5
          const base720 = duration >= 15 ? 1.5 : duration >= 10 ? 1.0 : 0.5
          return resolution === "1080p" ? base720 * 1.5 : base720
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "WaveSpeed: 720p $0.50/$1.00/$1.50 (5/10/15s), 1080p ×1.5 per video",
      },
      "kie-ai": {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          const duration = "duration" in params && typeof params.duration === "number" ? params.duration : 5
          if (resolution === "1080p") return duration >= 15 ? 1.575 : duration >= 10 ? 1.0475 : 0.5225
          return duration >= 15 ? 1.05 : duration >= 10 ? 0.7 : 0.35
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: 720p $0.35/$0.70/$1.05 (5/10/15s), 1080p $0.5225/$1.0475/$1.575 per video",
      },
    },
  },

  // Wan 2.6 I2V — Replicate (per-sec, primary) + WaveSpeed & Kie.ai (per-video by duration+resolution).
  [WavespeedVideoGenerationModelType.WAN_2_6_I2V]: {
    // No FAL endpoint — Replicate/WaveSpeed both cost ~$0.10/$0.15 per second. Rate held at
    // ~50% margin over that ($0.20/$0.30); product decision to keep the existing margin.
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      return resolution === "1080p" ? 0.3 : 0.2
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 2.6 I2V: $0.20/sec (720p) or $0.30/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          return resolution === "1080p" ? 0.15 : 0.1
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.10/sec (720p), $0.15/sec (1080p)",
      },
      wavespeed: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          const duration = "duration" in params && typeof params.duration === "number" ? params.duration : 5
          const base720 = duration >= 15 ? 1.5 : duration >= 10 ? 1.0 : 0.5
          return resolution === "1080p" ? base720 * 1.5 : base720
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "WaveSpeed: 720p $0.50/$1.00/$1.50 (5/10/15s), 1080p ×1.5 per video",
      },
      "kie-ai": {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          const duration = "duration" in params && typeof params.duration === "number" ? params.duration : 5
          if (resolution === "1080p") return duration >= 15 ? 1.575 : duration >= 10 ? 1.0475 : 0.5225
          return duration >= 15 ? 1.05 : duration >= 10 ? 0.7 : 0.35
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "Kie.ai: 720p $0.35/$0.70/$1.05 (5/10/15s), 1080p $0.5225/$1.0475/$1.575 per video",
      },
    },
  },

  // Wan 2.6 I2V Flash — Replicate (per-sec, primary) + WaveSpeed (per-video by duration+resolution).
  [WavespeedVideoGenerationModelType.WAN_2_6_I2V_FLASH]: {
    // No FAL endpoint — Replicate is the dearer routable provider ($0.05/$0.075 per second).
    // Rate held at ~50% margin over that ($0.10/$0.15); product decision to keep the margin.
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "720p"
      return resolution === "1080p" ? 0.15 : 0.1
    },
    unit: EPricingUnit.PER_SECOND,
    description: "Wan 2.6 I2V Flash: $0.10/sec (720p) or $0.15/sec (1080p)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          return resolution === "1080p" ? 0.075 : 0.05
        },
        unit: EPricingUnit.PER_SECOND,
        description: "Replicate: $0.05/sec (720p), $0.075/sec (1080p)",
      },
      wavespeed: {
        // WaveSpeed `alibaba/wan-2.6/image-to-video-flash`:
        //   base_price * duration/5 * (1080p ? 1.5 : 1) * (enable_audio ? 2 : 1), base_price $0.125.
        //
        // Our schema does NOT expose `enable_audio`, so we never send it and WaveSpeed applies its
        // own default — which is audio ON. Ledger (VM-1089, 2026-08-19): 1 row at 720p/5s billed
        // $0.25 against $0.125 recorded, exactly the 2x audio multiplier. The dead in-repo table
        // this replaces even labelled its $0.025/sec figure "with audio off".
        //
        // Billed as audio-on unless a caller explicitly disables it: that is both what the ledger
        // shows and the dearer, safe direction for a recorded provider cost.
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "720p"
          const duration = "duration" in params && typeof params.duration === "number" ? params.duration : 5
          const base720 = duration >= 15 ? 0.375 : duration >= 10 ? 0.25 : 0.125
          const byResolution = resolution === "1080p" ? base720 * 1.5 : base720
          const audioMultiplier = "enable_audio" in params && params.enable_audio === false ? 1 : 2
          return byResolution * audioMultiplier
        },
        unit: EPricingUnit.PER_REQUEST,
        description: "WaveSpeed: 720p $0.125/$0.25/$0.375 (5/10/15s), 1080p ×1.5, ×2 with audio (WaveSpeed's default)",
      },
    },
  },

  [SkyReelsVideoGenerationModelType.SKYREELS_V4_T2V]: {
    rate: SKYREELS_V4_RATE,
    unit: EPricingUnit.PER_SECOND,
    description: "SkyReels V4 T2V: $0.02/sec via WaveSpeed",
    providerCosts: {
      wavespeed: {
        // See `getSkyReelsV4WavespeedRate` — base $0.10/sec x mode x resolution. 720p normal is
        // $0.14/sec ($0.70 per 5s clip), 7x the old flat $0.02/sec. Consumer rate untouched
        // (SKYREELS_V4_RATE stays $0.02/sec) — the reprice is VM-1089 C5.
        rate: getSkyReelsV4WavespeedRate,
        unit: EPricingUnit.PER_SECOND,
        description: "WaveSpeed: $0.14/sec 720p, $0.11/sec 540p (normal mode); $0.35/sec 1080p",
      },
    },
  },
  [SkyReelsVideoGenerationModelType.SKYREELS_V4_I2V]: {
    rate: SKYREELS_V4_RATE,
    unit: EPricingUnit.PER_SECOND,
    description: "SkyReels V4 I2V: $0.02/sec via WaveSpeed",
    providerCosts: {
      wavespeed: {
        // See `getSkyReelsV4WavespeedRate` — base $0.10/sec x mode x resolution. 720p normal is
        // $0.14/sec ($0.70 per 5s clip), 7x the old flat $0.02/sec. Consumer rate untouched
        // (SKYREELS_V4_RATE stays $0.02/sec) — the reprice is VM-1089 C5.
        rate: getSkyReelsV4WavespeedRate,
        unit: EPricingUnit.PER_SECOND,
        description: "WaveSpeed: $0.14/sec 720p, $0.11/sec 540p (normal mode); $0.35/sec 1080p",
      },
    },
  },
  [SkyReelsVideoGenerationModelType.SKYREELS_V4_R2V]: {
    rate: SKYREELS_V4_RATE,
    unit: EPricingUnit.PER_SECOND,
    description: "SkyReels V4 R2V: $0.02/sec via WaveSpeed",
    providerCosts: {
      wavespeed: {
        // See `getSkyReelsV4WavespeedRate` — base $0.10/sec x mode x resolution. 720p normal is
        // $0.14/sec ($0.70 per 5s clip), 7x the old flat $0.02/sec. Consumer rate untouched
        // (SKYREELS_V4_RATE stays $0.02/sec) — the reprice is VM-1089 C5.
        rate: getSkyReelsV4WavespeedRate,
        unit: EPricingUnit.PER_SECOND,
        description: "WaveSpeed: $0.14/sec 720p, $0.11/sec 540p (normal mode); $0.35/sec 1080p",
      },
    },
  },
}

// Internal Replicate image costs - NEVER expose to frontend

export function calculateVideoCredits(
  modelId: VideoGenerationModelType | ELipsyncModel,
  params: VideoGenerationModelParams,
): number {
  const pricing = replicateModelPricing[modelId as VideoGenerationModelType]

  if (!pricing) {
    blg.warn(`Unknown model for credit calculation: ${modelId}, using default rate`)
    return 0.05 * 5 // default: $0.05 per second * 5 seconds
  }

  // Get the rate (handles both number and function)
  const rate = typeof pricing.rate === "function" ? pricing.rate(params) : pricing.rate

  // Use custom calculation if provided
  if (pricing.calculateCost) {
    return pricing.calculateCost(rate, params)
  }

  // Fall back to standard calculation based on unit type
  switch (pricing.unit) {
    case EPricingUnit.PER_SECOND:
      // Fixed duration models
      if (pricing.fixedDuration) {
        return rate * pricing.fixedDuration
      }
      // Frame-based models (like Wavespeed)
      if ("num_frames" in params && "frames_per_second" in params) {
        return calculatePerSecondWithFrames(rate, params)
      }
      // Standard duration-based models
      return calculatePerSecond(rate, params)

    case EPricingUnit.PER_REQUEST:
      return calculatePerRequest(rate, params)

    case EPricingUnit.PER_FRAME:
      return calculatePerFrame(rate, params)

    case EPricingUnit.PER_IMAGE:
      return rate // video models don't use per-image pricing, return flat rate

    // Token/character units belong to the LLM and TTS registries in `modalityPricing.ts`;
    // an image/video model can never carry them. Enumerated so the switch stays exhaustive.
    case EPricingUnit.PER_TOKEN:
    case EPricingUnit.PER_CHARACTER:
      return rate
  }
}

// Credit conversion: 1 credit = $0.05

/**
 * Consumer markup applied on top of the model's internal (top-level `rate`) cost.
 *
 * PRICING INVARIANT (enforced by pricing.margin.unit.test.ts): for every model and every
 * schema-valid param scenario,
 *
 *   calculateVideoCredits(model, params) >= getVideoProviderCost(model, provider, params)
 *
 * for every PRODUCTION-ROUTABLE provider — i.e. the PRE-markup rate must already cover the
 * provider bill, so this multiplier lands as a real 20% margin rather than as the entire
 * difference between cost and price. Sizing the rate so that only `1.2 * rate` clears the
 * provider cost would leave a 0% margin: break-even, not profit.
 */

export function calculateVideoGenerationCredits(
  modelId: VideoGenerationModelType,
  params: VideoGenerationModelParams,
): number {
  const internalCost = calculateVideoCredits(modelId, params)
  return convertUsdToCredits(internalCost)
}

/**
 * Calculate credits for image generation with markup applied
 * This is the user-facing credit cost including markup
 */

export function getVideoProviderCost(
  modelId: VideoGenerationModelType | ELipsyncModel,
  provider: string,
  params: VideoGenerationModelParams,
): number | null {
  const pricing = replicateModelPricing[modelId as VideoGenerationModelType]
  if (!pricing?.providerCosts?.[provider]) return null

  const entry = pricing.providerCosts[provider]
  const rate = typeof entry.rate === "function" ? entry.rate(params) : entry.rate

  // Use custom calculation if provided (e.g., Kie.ai flat-rate pricing)
  if (entry.calculateCost) {
    return entry.calculateCost(rate, params)
  }

  // Standard unit-based calculation using existing helpers
  switch (entry.unit) {
    case EPricingUnit.PER_SECOND:
      if ("num_frames" in params && "frames_per_second" in params) {
        return calculatePerSecondWithFrames(rate, params)
      }
      return calculatePerSecond(rate, params)
    case EPricingUnit.PER_REQUEST:
      return calculatePerRequest(rate, params)
    case EPricingUnit.PER_FRAME:
      return calculatePerFrame(rate, params)
    case EPricingUnit.PER_IMAGE:
      return rate // video provider costs don't use per-image, return flat rate

    // Token/character units belong to the LLM and TTS registries in `modalityPricing.ts`;
    // an image/video model can never carry them. Enumerated so the switch stays exhaustive.
    case EPricingUnit.PER_TOKEN:
    case EPricingUnit.PER_CHARACTER:
      return rate
  }
}

/**
 * The unit a provider bills this video call in, plus the billable QUANTITY in that unit.
 *
 * Persisted per job as `billingUnit` / `billingQuantity` so a provider invoice line can be checked
 * against what we believe we consumed (VM-1012). The unit is the registry's own declared
 * `providerCosts[provider].unit` — the same value `getVideoProviderCost` switches on — so a unit
 * can never drift from the rate it prices. The quantity reuses the exact same param helpers at
 * rate = 1, which makes the quantity the multiplicand of that price by construction.
 *
 * Models with a custom `calculateCost` still report their declared unit and param-derived
 * quantity: quantity is a USAGE fact (seconds of output), and dollars for those models are not a
 * plain rate × quantity product. Cost stays `getVideoProviderCost`'s job; this never re-derives it.
 */
export function getVideoProviderBilling(
  modelId: VideoGenerationModelType | ELipsyncModel,
  provider: string,
  params: VideoGenerationModelParams,
): { unit: EPricingUnit; quantity: number } | null {
  const pricing = replicateModelPricing[modelId as VideoGenerationModelType]
  if (!pricing?.providerCosts?.[provider]) return null

  const entry = pricing.providerCosts[provider]

  switch (entry.unit) {
    case EPricingUnit.PER_SECOND: {
      let quantity: number
      if (entry.requiresActualDurationSec) {
        quantity = calculateMeasuredVideoCost(1, params)
      } else if ("num_frames" in params && "frames_per_second" in params) {
        quantity = calculatePerSecondWithFrames(1, params)
      } else {
        quantity = calculatePerSecond(1, params)
      }
      return { unit: EPricingUnit.PER_SECOND, quantity }
    }
    case EPricingUnit.PER_FRAME:
      return { unit: EPricingUnit.PER_FRAME, quantity: calculatePerFrame(1, params) }
    // A video model priced per-image or per-request bills one flat call — mirrors the flat-rate
    // branches of `getVideoProviderCost` above.
    case EPricingUnit.PER_REQUEST:
    case EPricingUnit.PER_IMAGE:
    case EPricingUnit.PER_TOKEN:
    case EPricingUnit.PER_CHARACTER:
      return { unit: EPricingUnit.PER_REQUEST, quantity: 1 }
  }
}

/**
 * Calculate the actual provider cost for an image generation request.
 * Returns null if the model or provider has no pricing data.
 */
